// Procedural pixel-art sprite atlas. Sprites are authored as ASCII pixel maps
// over shared "shapes"; each entity gets its own palette so every weapon, enemy,
// boss, character, pickup and projectile has a distinct hand-defined sprite.
// All sprites bake into one canvas texture (an atlas) used by the sprite batch.

import * as THREE from 'three'

const CELL = 24 // px per sprite cell in the atlas

// Palette keys used across shapes:
//   O = outline (dark)   A = main body    B = shade    H = highlight
//   E = eye / detail     C = accent       . / space = transparent
export type Pal = Record<string, string>

// ---- shared shapes (16-22px, centered in a 24px cell) ----
export const SHAPES: Record<string, string[]> = {
  // round slime / blob
  slime: [
    '                        ',
    '          OOOOO         ',
    '       OOOOHHHOOOO      ',
    '      OOHHHHHHHHAOO     ',
    '     OOHHHHHHHAAAAOO    ',
    '    OOHHHHHHHAAAAAAOO   ',
    '   OOHHHHHHHAAAAAAAAOO  ',
    '   OHHHHHHHAAAAAAAAABO  ',
    '   OHHHHHHAAAAAAAAAABO  ',
    '   OHHHHEEEAAAEEEAABBO  ',
    '  OOHHHHEEEAAAEEEABBBOO ',
    '  OHHHHAEEEAAAEEEBBBBBO ',
    '  OHHHAAEEEAAAEEEBBBBBO ',
    '  OHHAAAAAAAAAABBBBBBBO ',
    '  OHAAAAAAAAAABBBBBBBBO ',
    '  OHAAAAAAAAABBBBBBBBBO ',
    '  OAAAAAAAAABBBBBBBBBBO ',
    '  OOABAAAAABBBBBBBBBBOO ',
    '   OOOHAAABBBBBBBBBOOO  ',
    '    OHAAABBBBBBBBBBBO   ',
    '    OABBBBBBBBBBBBBBO   ',
    '    OOOOOOOOOOOOOOOOO   ',
    '                        ',
    '                        ',
  ],
  // small slime
  slimesmall: [
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '           OOOOO        ',
    '         OOOHHAOOO      ',
    '        OOHHHAAHAOO     ',
    '       OOHHAAAAAAAOO    ',
    '      OOHHAAAAAAAABOO   ',
    '      OHHAAAAAAAAABBO   ',
    '      OHAAEEEAEEEBBBO   ',
    '      OHAAEEEAEEEBBBO   ',
    '      OHAAAAAAABBBBBO   ',
    '      OHAAAAAABBBBBBO   ',
    '      OAAAAAABBBBBBBO   ',
    '      OOAAAABBBBBBBOO   ',
    '       OOAABBBBBBBOO    ',
    '        OBBBBBBBBBO     ',
    '        OOOOOOOOOOO     ',
    '                        ',
    '                        ',
    '                        ',
  ],
  // bipedal brute / humanoid
  biped: [
    '                        ',
    '          OOOOO         ',
    '         OOHHHOO        ',
    '        OOHHHHAOO       ',
    '        OHEEHEEAO       ',
    '        OHEEHEEBO       ',
    '       OOHHHAAABOO      ',
    '      OOHHHAAAAAAOO     ',
    '     OOHHHAAAAAAAAOO    ',
    '     OHHHAAAAAAAAABO    ',
    '    OOHHAAAAAAAAAABOO   ',
    '    OHHAAAAAAAAAABBBO   ',
    '   OOHAAAAAAAAAABBBBOO  ',
    '  OOHHAAAAAAAAABBBBBBOO ',
    '  OHHAAAAAAAAABBBBBBBBO ',
    '  OHAAAAAAAAABBBBBBBBBO ',
    '  OAABBBAAABBBBBBBBBBBO ',
    '  OOABOOAAABOABBBOOBBOO ',
    '   OOOOOAABBOABBBOOOOO  ',
    '       OABBBOABBBO      ',
    '       OABBBOABBBO      ',
    '       OBBBBOBBBBO      ',
    '       OOOOOOOOOOO      ',
    '                        ',
  ],
  // floating ghost / wisp
  flyer: [
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '  OOOO    OOOOO    OOOO ',
    '  OHHOOOOOOHHAOOOOOOAAO ',
    '  OHHHHOOHHHAAHAOOHAABO ',
    '  OHHHHHHHHAAAAAHHAAABO ',
    '  OHHHHHHHAAAAAAAAAABBO ',
    '  OHHHHHHEEEAEEEAAABBBO ',
    '  OHHHHHAEEEAEEEAABBBBO ',
    '  OHAAHAAAAAAAAAABBBBBO ',
    '  OOOOABAAAAAAAABBBOOOO ',
    '     OOOAAAAAAABBOOO    ',
    '       OOABAABBBOO      ',
    '        OAOABBOBO       ',
    '       OOAOOBOOBOO      ',
    '       OAAOOBOOABO      ',
    '       OABOOBOOBBO      ',
    '       OOOOOBOOOOO      ',
    '           OBO          ',
    '           OOO          ',
    '                        ',
    '                        ',
  ],
  // robed caster / shooter
  caster: [
    '                        ',
    '            OOO         ',
    '           OOHOO        ',
    '          OOHHAOO       ',
    '         OOHHHHAO       ',
    '         OHHHHABOO      ',
    '        OOHHHAAAAOO     ',
    '       OOHHHAAAAAAOO    ',
    '      OOHHHAAAAAAAAO    ',
    '      OHHHACCACCAABO    ',
    '      OOHAACAAACAABO    ',
    '       OAAAAAAAAABBO    ',
    '       OOHAAAAAAABOO    ',
    '       OHHAAAAAABBOO    ',
    '       OHAAAAAABBBBO    ',
    '      OOHAAACCCBBBBO    ',
    '      OHHAAACCCBBBBOO   ',
    '      OHAAAACCCBBBBBO   ',
    '     OOHAAABCCCBBBBBO   ',
    '     OHAAABBBBBBBBBBOO  ',
    '     OAAABBBBBBBBBBBBO  ',
    '     OABBBBBBBBBBBBBBO  ',
    '     OOOOOOOOOOOOOOOOO  ',
    '                        ',
  ],
  // bomb monster
  bomb: [
    '                 OOCCOO ',
    '               OOOCCCCO ',
    '              OOHACCCCO ',
    '              OHABOCCOO ',
    '          OOOOOABOOOOO  ',
    '        OOOHHHHAOO      ',
    '      OOOHHHAAABOOO     ',
    '      OHHHHAAAAAHAO     ',
    '     OOHHHAAAAAAABOO    ',
    '     OHHHAAAAAAAAABO    ',
    '    OOHHAAAAAAAAAABOO   ',
    '    OHHAAEEAAAEEABBBO   ',
    '    OHAAAEEAAAEEBBBBO   ',
    '    OAAAAAAAAAABBBBBO   ',
    '    OOHAAAAAAABBBBBOO   ',
    '     OAAAAAAABBBBBBO    ',
    '     OOHAAAABBBBBBOO    ',
    '      OABAABBBBBBBO     ',
    '      OOOBBBBBBBOOO     ',
    '       OOBOBBBOBOO      ',
    '       OAABOOOABBO      ',
    '       OBBBO OBBBO      ',
    '       OOBOO OOBOO      ',
    '        OOO   OOO       ',
  ],
  // jagged crystal
  crystal: [
    '                        ',
    '           OOO          ',
    '          OOHOO         ',
    '         OOHHAO         ',
    '         OHHHAOO        ',
    '        OOCHHAAOO       ',
    '       OOHCHAAAAO       ',
    '       OHHHAAAABOO      ',
    '       OHHAAAAAAAO      ',
    '       OHAAAEAAABO      ',
    '       OHAAEEEAABOO     ',
    '     OOOHAAEEEAAABOO    ',
    '    OOAOHAAAEAAABBBOOO  ',
    '   OOHAOAAAAAAABBBBABOO ',
    '   OHHBOOHAAAABBOABBBBO ',
    '   OAABOOHAAABBBOABBBBO ',
    '   OOHBOOAAABBBBOABBBOO ',
    '    OABOOAABBBBBOABBOO  ',
    '    OOAOOBBBBBBBOBBOO   ',
    '     OOOOOABBBBOOBOO    ',
    '         OBBBBBOOOO     ',
    '         OOOOOOO        ',
    '                        ',
    '                        ',
  ],
  // skeleton / undead
  skelly: [
    '           OOO          ',
    '         OOOHOOO        ',
    '        OOHHHHAOO       ',
    '        OHHHHHHAO       ',
    '       OOHEEHEEBOO      ',
    '       OHEEEHEEEAO      ',
    '       OOHEEEEEBOO      ',
    '        OAHAEABBO       ',
    '        OOHAAABOO       ',
    '         OHAAABO        ',
    '         OAAABBO        ',
    '       OOOOHABOOOO      ',
    '      OOAAAABBABBOO     ',
    '      OHAOOOAOOOABO     ',
    '     OOHBAAAABBBABOO    ',
    '     OHABOOOBOOOBBBO    ',
    '    OOABOOOOBOOOOBBOO   ',
    '   OOHAOOOBBBBBOOOBBOO  ',
    '   OAABO OBOOOBO OBBBO  ',
    '   OOAOO OBO OBO OOBOO  ',
    '    OOO  OBO OBO  OOO   ',
    '         OBOOOBO        ',
    '         OBBOBBO        ',
    '         OBOOOBO        ',
  ],
  // hefty boss blob (drawn near full cell, scaled big in-world)
  boss: [
    '     OOOOOOOO     ',
    '   OOAAAAAAAAOO   ',
    '  OAAAAAAAAAAAAO  ',
    ' OAAAHHAAAAHHAAAO ',
    ' OAAHHHAAAAHHHAAO ',
    ' OAAAEAAAAAAEAAAO ',
    'OAAAAAAAAAAAAAAAAO',
    'OAACAAAAAAAAAACAAO',
    'OAAAAAAAAAAAAAAAAO',
    'OAAABBAAAAAABBAAAO',
    ' OAABBBAAAABBBAAO ',
    ' OAAABBBBBBBBAAAO ',
    '  OAAAABBBBAAAAO  ',
    '   OOAAAAAAAAOO   ',
    '     OOOOOOOO     ',
    '                  ',
  ],
  // player hero (tintable body)
  hero: [
    '      OOOO      ',
    '     OAAAAO     ',
    '     OAEEAO     ',
    '     OAAAAO     ',
    '    OOAAAAOO    ',
    '   OCAAAAAACO   ',
    '   OCAAAAAACO   ',
    '   OAAHHHHAAO   ',
    '   OAAAAAAAAO   ',
    '   OAACAACAAO   ',
    '    OAAOOAA O   ',
    '    OAAO OAAO   ',
    '    OAAO OAAO   ',
    '   OOAOO OOAOO  ',
    '                ',
    '                ',
  ],
  // pickups
  gem: [
    '                        ',
    '                        ',
    '                        ',
    '        OOOOOOOOO       ',
    '       OOHHHHHHAOO      ',
    '      OOHHHHHAAAAOO     ',
    '     OOHHHHHAAAAAAOO    ',
    '    OOHHHHHAAAAAAAAOO   ',
    '   OOHHHHHAAAAAAAAABOO  ',
    '   OHHHHHAAAAAAAAAABBO  ',
    '   OHHHHAAAAAAAAAABBBO  ',
    '   OOAHAAAAAAAAAABBBOO  ',
    '    OOAAAAAAAAAABBBOO   ',
    '     OOHAAAAAAABBBOO    ',
    '      OAAAAAAABBBBO     ',
    '      OOAAAAABBBBOO     ',
    '       OOAAABBBBOO      ',
    '        OOBBBBBOO       ',
    '         OOABBOO        ',
    '          OBBBO         ',
    '          OOBOO         ',
    '           OOO          ',
    '                        ',
    '                        ',
  ],
  coin: [
    '                        ',
    '         OOOOOOO        ',
    '       OOOHHHHAOOO      ',
    '     OOOHCCCCCCCAOOO    ',
    '    OOHCCCHHHHACCCAOO   ',
    '    OHCCHHHHHAAAACCAO   ',
    '   OOCCHHHHHAAAAAACCOO  ',
    '   OHCHHHHHACAAAAAACBO  ',
    '  OOCCHHHHAACAAAAAACCOO ',
    '  OHCHHHHAACCCAAAAABCBO ',
    '  OHCHHHACCCCCCCAABBCBO ',
    '  OHCHHAAACCCCCAABBBCBO ',
    '  OHCHAAAACCCCCABBBBCBO ',
    '  OACAAAAACCCCCBBBBBCBO ',
    '  OOCCAAAACAAACBBBBCCOO ',
    '   OACAAAAAAABBBBBBCBO  ',
    '   OOCCAAAAABBBBBBCCOO  ',
    '    OACCAAABBBBBBCCBO   ',
    '    OOACCCBBBBBCCCBOO   ',
    '     OOOBCCCCCCCBOOO    ',
    '       OOOBBBBBOOO      ',
    '         OOOOOOO        ',
    '                        ',
    '                        ',
  ],
  heart: [
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '       OOOOOOOOOOOOO    ',
    '      OOHHHHAOHHHHAOO   ',
    '     OOHHHHHHHHAAAAAOO  ',
    '     OHHHHHHAAAAAAAAAO  ',
    '     OHHHHHAAAAAAAAABO  ',
    '     OHHHHAAAAAAAAAABO  ',
    '     OHHHAAAAAAAAAABBO  ',
    '     OOAAAAAAAAAAABBOO  ',
    '      OOAAAAAAAAABBOO   ',
    '       OOHAAAAAABBOO    ',
    '        OAAAAAABBBO     ',
    '        OOAAAABBBOO     ',
    '         OOAABBBOO      ',
    '          OBBBBBO       ',
    '          OOBBBOO       ',
    '           OOBOO        ',
    '            OBO         ',
    '            OOO         ',
    '                        ',
    '                        ',
  ],
  chest: [
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '       OOOOOOOOOOOOO    ',
    '      OOHHHHHHHHHHAOO   ',
    '     OOHHHHHAAAAAAAAOO  ',
    '     OHHHHHAAAAAAAAABO  ',
    '     OHHHHAAAAAAAAAABO  ',
    '     OHHHAAACCCAAAABBO  ',
    '     OHHAAAACCCAAABBBO  ',
    '     OHAAAAACCCAABBBBO  ',
    '     OHAAAAACCCABBBBBO  ',
    '     OHAAAAACCCBBBBBBO  ',
    '     OHAAAAAAABBBBBBBO  ',
    '     OHAAAAAABBBBBBBBO  ',
    '     OHAAAAABBBBBBBBBO  ',
    '     OHAAAABBBBBBBBBBO  ',
    '     OHAAABBBBBBBBBBBO  ',
    '     OABBBBBBBBBBBBBBO  ',
    '     OOOOOOOOOOOOOOOOO  ',
    '                        ',
    '                        ',
  ],
  magnet: [
    '          OOOOO         ',
    '       OOOOHHHOOOO      ',
    '      OOHHHHHHHHAOO     ',
    '     OOHHHHHHHHAAAOO    ',
    '    OOHHHHHHHHAAAAAOO   ',
    '   OOHHHHAAAAABAAAAAOO  ',
    '   OHHHHAAOOOOOAAAAAAO  ',
    '   OHHHAAOO   OOAAAABO  ',
    '  OOHHHAOO     OOAAABOO ',
    '  OHHHHAO       OAABBBO ',
    '  OHHHHAO       OABBBBO ',
    '  OHHHHAO       OABBBBO ',
    '  OHHHABO       OABBBBO ',
    '  OHHAABO       OABBBBO ',
    '  OHAAABO       OABBBBO ',
    '  OHAAABO       OABBBBO ',
    '  OCCCCCO       OCCCCCO ',
    '  OCCCCCO       OCCCCCO ',
    '  OCCCCCO       OCCCCCO ',
    '  OOOOOOO       OOOOOOO ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
  ],
  // shrine / altar totem (tinted per type at runtime)
  shrine: [
    '                        ',
    '                        ',
    '                        ',
    '            OOOOO       ',
    '            OAAAOO      ',
    '            OHHABO      ',
    '            OHHABO      ',
    '            OHHABO      ',
    '            OHHABO      ',
    '            OHHABO      ',
    '            OHHABO      ',
    '           OOHHABO      ',
    '           OAHHABO      ',
    '           OAHHABO      ',
    '           OAHHABO      ',
    '           OAHHABO      ',
    '           OAHHABO      ',
    '           OAHHABO      ',
    '           OAHHABO      ',
    '           OAHHABO      ',
    '         OOOAHHABOOOO   ',
    '         OAAAAAAAAAAO   ',
    '         OAAAAAAAAAAO   ',
    '         OOOOOOOOOOOO   ',
  ],
  // ---- environment props ----
  tree: [
    '                        ',
    '                        ',
    '          OOOOOOOO      ',
    '         OOAAAAAAOO     ',
    '        OOAHAAAAAAOO    ',
    '       OOHHHHHAAAAAOO   ',
    '       OAHHHHHAAAAAAO   ',
    '       OAHHHHHAAAAAAO   ',
    '       OAHHHHHHHHHAAO   ',
    '       OAAAHAAHHHHAAO   ',
    '       OAAAAAAHHHHAAO   ',
    '       OAAAAAAAAAAAAO   ',
    '       OOAAAAAAAAAAOO   ',
    '        OOAAAAAAAAOO    ',
    '         OOAAAAAAOO     ',
    '          OOBBBBOO      ',
    '           OBBBBO       ',
    '           OBBBBO       ',
    '           OBBBBO       ',
    '           OBBBBO       ',
    '           OBBBBO       ',
    '           OBBBBO       ',
    '           OOOOOO       ',
    '                        ',
  ],
  bush: [
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '         OOOOOOOOOO     ',
    '       OOOHHAAAAAAOOO   ',
    '       OAHHHHAAAHAAAO   ',
    '      OOAHHHHAAHHHAAOO  ',
    '      OAAHHHHAAHHHAAAO  ',
    '      OAAAHHAAAAHAAAAO  ',
    '      OAAAAAAAAAAAAAAO  ',
    '      OOAAAAAAAAAAAAOO  ',
    '       OAAAAAAAAAAAAO   ',
    '       OOOAAAAAAAAOOO   ',
    '         OOOOOOOOOO     ',
    '                        ',
    '                        ',
    '                        ',
  ],
  rock: [
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '          OOOO          ',
    '        OOOAAOOOO       ',
    '       OOHHHAAAAOOO     ',
    '      OOHHHHHAAAAAOO    ',
    '      OAHHHHHAAAAAAO    ',
    '     OOAHHHHHAAAAAAOO   ',
    '     OAAAHHHAAAAAAAAO   ',
    '     OAAAAAAAAAAAAAAOO  ',
    '    OOAAAAAAAAAAAAAAAO  ',
    '    OAAAAAAAAAAAAAAAAO  ',
    '    OAAAAAAAAAAAAAAAOO  ',
    '    OABBBBBBBBBBBBBBBO  ',
    '    OOBBBBBBBBBBBBBBBO  ',
    '     OOOOOOOOOOOOOOOOO  ',
    '                        ',
    '                        ',
    '                        ',
  ],
  cactus: [
    '                        ',
    '                        ',
    '                        ',
    '           OOOOOO       ',
    '           OAAAAO       ',
    '           OHAAAOOOOO   ',
    '       OOOOOHAAAOOAAO   ',
    '       OAAOOHAAAOOAAO   ',
    '       OHAOOHAAAOOAAO   ',
    '       OHAOOHAAAAAAAO   ',
    '       OHAAAHAAAAAAAO   ',
    '       OHAAAHAAAAAAAO   ',
    '       OAAAAHAAAOOOOO   ',
    '       OOOOOHAAAO       ',
    '           OHAAAO       ',
    '           OHAAAO       ',
    '           OHAAAO       ',
    '           OHAAAO       ',
    '           OHAAAO       ',
    '           OHAAAO       ',
    '           OHAAAO       ',
    '           OAAAAO       ',
    '           OOOOOO       ',
    '                        ',
  ],
  grave: [
    '                        ',
    '                        ',
    '                        ',
    '         OOOOOOOO       ',
    '        OOAAAAAAOO      ',
    '       OOAAAAAAAAOO     ',
    '      OOAAAABBAAAAOO    ',
    '      OAAAAABBAAAAAO    ',
    '      OAABBBBBBBBAAO    ',
    '      OAHHBBBBBBBAAO    ',
    '      OAHHAABBAAAAAO    ',
    '      OAHHAABBAAAAAO    ',
    '      OAHHAABBAAAAAO    ',
    '      OAAAAABBAAAAAO    ',
    '      OAAAAABBAAAAAO    ',
    '      OAAAAABBAAAAAO    ',
    '      OAAAAABBAAAAAO    ',
    '      OAAAAAAAAAAAAO    ',
    '   OOOOAAAAAAAAAAAAOOOO ',
    '   OBBBBBBBBBBBBBBBBBBO ',
    '   OBBBBBBBBBBBBBBBBBBO ',
    '   OBBBBBBBBBBBBBBBBBBO ',
    '   OOOOOOOOOOOOOOOOOOOO ',
    '                        ',
  ],
  pillar: [
    '                        ',
    '                        ',
    '                        ',
    '          OOOO OOO      ',
    '         OOAAOOOAO      ',
    '         OAAAAAAAO      ',
    '         OHHAAAAAOO     ',
    '         OHHAAAABBO     ',
    '         OHHAAAABBO     ',
    '         OHHAAAABBO     ',
    '         OHHAAAABBO     ',
    '         OHHAAAABBO     ',
    '         OHHAAAABBO     ',
    '         OHHAAAABBO     ',
    '         OHHAAAABBO     ',
    '         OHHAAAABBO     ',
    '         OHHAAAABBO     ',
    '         OHHAAAABBO     ',
    '         OHHAAAABBO     ',
    '         OHHAAAABBO     ',
    '       OOOHHAAAABBOOO   ',
    '       OAAAAAAAAAAAAO   ',
    '       OBBBBBBBBBBBBO   ',
    '       OOOOOOOOOOOOOO   ',
  ],
  // ---- space props ----
  crater: [
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '        OOOOOOOOOO      ',
    '      OOOHHHHHHHHOOO    ',
    '    OOOHHAAAAAAAAHHOOO  ',
    '    OHHAAAAAAAAAAAAHHO  ',
    '   OOAAAABBBBBBBBAAAAOO ',
    '   OAAAABBBBBBBBBBAAAAO ',
    '   OAAABBBBBBBBBBBBAAAO ',
    '   OAAABBBBBBBBBBBBAAAO ',
    '   OOAAABBBBBBBBBBAAAOO ',
    '    OOAAABBBBBBBBAAAOO  ',
    '     OOOAAAAAAAAAAOOO   ',
    '       OOOOOOOOOOOO     ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
  ],
  flag: [
    '                        ',
    '                        ',
    '           OOOO         ',
    '           OAAOOO       ',
    '           OAAHHOOOOO   ',
    '           OAAHHHHHHOO  ',
    '           OAAHHHHHHHO  ',
    '           OAAHHHCHHOO  ',
    '           OAAHHCCCOO   ',
    '           OAAHHHCOO    ',
    '           OAAHHOOO     ',
    '           OAAHOO       ',
    '           OAAOO        ',
    '           OAAO         ',
    '           OAAO         ',
    '           OAAO         ',
    '           OAAO         ',
    '           OAAO         ',
    '           OAAO         ',
    '           OAAO         ',
    '           OAAO         ',
    '           OAAO         ',
    '           OAAO         ',
    '           OOOO         ',
  ],
  satellite: [
    '                        ',
    '                        ',
    '                        ',
    '         OOOOOO         ',
    '         OCCCCO         ',
    '        OOCCCCOO        ',
    '        OCCCCCCO        ',
    '        OOCAACOO        ',
    '         OCAACO         ',
    ' OOOOOOOOOOAAOOOOOOOOOO ',
    ' OHHHHHHOAAAAAAOHHHHHHO ',
    ' OHHHHHHAAAAAAAAHHHHHHO ',
    ' OHHHHHHAAAAAAAAHHHHHHO ',
    ' OHHHHHHAAAAAAAAHHHHHHO ',
    ' OHHHHHHAAAAAAAAHHHHHHO ',
    ' OHHHHHHAAAAAAAAHHHHHHO ',
    ' OOOOOOOOAAAAAAOOOOOOOO ',
    '        OOOOOOOO        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
  ],
  crate: [
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '    OOOOOOOOOOOOOOOOOO  ',
    '    OHHHHHHHBBHHHHHHHO  ',
    '    OHHHHHHHBBHHHHHHHO  ',
    '    OAAAAAAABBAAAAAAAO  ',
    '    OAAAAAAABBAAAAAAAO  ',
    '    OAAAAAAABBAAAAAAAO  ',
    '    OAAAAAAABBAAAAAAAO  ',
    '    OBBBBBBBBBBBBBBBBO  ',
    '    OBBBBBBBBBBBBBBBBO  ',
    '    OAAAAAAABBAAAAAAAO  ',
    '    OAAAAAAABBAAAAAAAO  ',
    '    OAAAAAAABBAAAAAAAO  ',
    '    OAAAAAAABBAAAAAAAO  ',
    '    OAAAAAAABBAAAAAAAO  ',
    '    OABAAAAABBAAAAABAO  ',
    '    OAAAAAAABBAAAAAAAO  ',
    '    OOOOOOOOOOOOOOOOOO  ',
    '                        ',
    '                        ',
  ],
  antenna: [
    '                        ',
    '                        ',
    '            OOOO  OOO   ',
    '            OCCOOOOHO   ',
    '            OCCOOHHHO   ',
    '            OCCHHHHHO   ',
    '            OAAAAAAAO   ',
    '            OAAOOHHHO   ',
    '            OAAOOOOHO   ',
    '            OAAO  OOO   ',
    '            OAAO        ',
    '            OAAO        ',
    '            OAAO        ',
    '            OAAO        ',
    '            OAAO        ',
    '            OAAO        ',
    '            OAAO        ',
    '            OAAO        ',
    '            OAAO        ',
    '         OOOOAAOOOO     ',
    '         OAAAAAAAAO     ',
    '         OAAAAAAAAO     ',
    '         OOOOOOOOOO     ',
    '                        ',
  ],
  moai: [
    '                        ',
    '                        ',
    '                        ',
    '           OOOOOOO      ',
    '          OOAAAAAOO     ',
    '         OOAAAAAAAO     ',
    '         OHHHAAAAAO     ',
    '         OHHHAAAAAOO    ',
    '         OHHHAAAAAAOO   ',
    '         OHHHAAAABBBO   ',
    '         OHHHAAAABBBO   ',
    '         OHEEEAAEEEBO   ',
    '         OHEEEAAEEEBO   ',
    '         OAEEEAAEEEBO   ',
    '         OAAAAAAABBBO   ',
    '         OAAAAAAABBBO   ',
    '         OAAAAAAABBBO   ',
    '         OAAAAAAABBBO   ',
    '         OAAAAAAABBBO   ',
    '         OAAAAAAABBBO   ',
    '         OAAAAAAABBBO   ',
    '         OAAAAAAABBBO   ',
    '         OOOOOOOOOOOO   ',
    '                        ',
  ],
  microwave: [
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '   OOOOOOOOOOOOOOOOOOOO ',
    '   OAAAAAAAAAAAAAAAAAAO ',
    '   OAAAAAAAAAAAAAAAAAAO ',
    '   OAAHHHHHHHHHACCCCCAO ',
    '   OAAHHHHHHHHHACCCCCAO ',
    '   OAAHHHHHHHHHACCCCCAO ',
    '   OAAHHHHHHHHHACCCCCAO ',
    '   OAAHHHHHHHHHAAAAAAAO ',
    '   OAAHHHHHHHHHAEEEEEAO ',
    '   OAAHHHHHHHHHAEEEEEAO ',
    '   OAAHHHHHHHHHAEEEEEAO ',
    '   OAAHHHHHHHHHAEEEEEAO ',
    '   OAAAAAAAAAAAAAAAAAAO ',
    '   OAAAAAAAAAAAAAAAAAAO ',
    '   OAAAAAAAAAAAAAAAAAAO ',
    '   OOAAAOOOOOOOOOOAAAOO ',
    '    OOOOO        OOOOO  ',
    '                        ',
    '                        ',
  ],
  // astronaut hero (helmet + visor; suit tinted per character)
  astronaut: [
    '                        ',
    '         OOOOOOO        ',
    '        OOHHHHAOO       ',
    '        OHHHHHHAO       ',
    '       OOVVVVVVVOO      ',
    '       OHVVVVVVVAO      ',
    '       OHVVVVVVVBO      ',
    '       OOHHAAAABOO      ',
    '       OOHAAAAABOO      ',
    '      OOHHAAAAAAAOO     ',
    '     OOHHAAAAAAAABOO    ',
    '     OHHAAAACAAAABBO    ',
    '    OOHAAAACCCAABBBOO   ',
    '   OOHHAAAACCCABBBBBOO  ',
    '   OHHAAAAAACABBBBBBBO  ',
    '   OHABBAAAAABBBBBBBBO  ',
    '   OHABOAAABBBBBBOABBO  ',
    '   OABBOOAABOABBOOBBBO  ',
    '   OOOOOOABBOABBOOOOOO  ',
    '        OABBOABBO       ',
    '        OABBOABBO       ',
    '        OBBBOBBBO       ',
    '        OOOOOOOOO       ',
    '                        ',
  ],
  // projectile shapes (drawn white-ish; tinted per weapon at runtime)
  orb: [
    '                        ',
    '           OOO          ',
    '       OOOOOHOOOOO      ',
    '      OOHHHHHHHHAOO     ',
    '     OOHHHHHHHAAAAOO    ',
    '    OOHHHHHHHAAAAAAOO   ',
    '   OOHHHHHHHAAAAAAAAOO  ',
    '   OHHHHHHHAAAAAAAAABO  ',
    '   OHHHHHHAAAAAAAAAABO  ',
    '   OHHHHHAAAAAAAAAABBO  ',
    '  OOHHHHAAAAAAAAAABBBOO ',
    '  OHHHHAAAAAAAAAABBBBBO ',
    '  OOHHAAAAAAAAAABBBBBOO ',
    '   OHAAAAAAAAAABBBBBBO  ',
    '   OHAAAAAAAAABBBBBBBO  ',
    '   OAAAAAAAAABBBBBBBBO  ',
    '   OOAAAAAAABBBBBBBBOO  ',
    '    OOAAAAABBBBBBBBOO   ',
    '     OOAAABBBBBBBBOO    ',
    '      OOBBBBBBBBBOO     ',
    '       OOOOOBOOOOO      ',
    '           OOO          ',
    '                        ',
    '                        ',
  ],
  blade: [
    '          OOO           ',
    '          OHO           ',
    '          OHO           ',
    '          OHO           ',
    '         OOAOO          ',
    '         OHHAO          ',
    '         OHABO          ',
    '         OHABO          ',
    '        OOHABOO         ',
    '    OOOOOHABAAOOOOO     ',
    'OOOOOHHHHABOAAAAABOOOOO ',
    'OHHHAHAAABOOOAAABBBBBBO ',
    'OOOOOABBBAAOAABBBBOOOOO ',
    '    OOOOOAAAABOOOOO     ',
    '        OOAABOO         ',
    '         OAABO          ',
    '         OABBO          ',
    '         OBBBO          ',
    '         OOBOO          ',
    '          OBO           ',
    '          OBO           ',
    '          OBO           ',
    '          OOO           ',
    '                        ',
  ],
  bolt: [
    '                OOOO    ',
    '               OOHAOO   ',
    '              OOHHAAO   ',
    '             OOHHAABO   ',
    '             OHHAABBO   ',
    '            OOHAABBOO   ',
    '           OOHHABBOO    ',
    '           OHHAABOO     ',
    '          OOHAAABO      ',
    '         OOHHAAABOO     ',
    '         OHHAAAAAAO     ',
    '         OHAAAAAABO     ',
    '         OAAAAAABBO     ',
    '         OOAAAAABOO     ',
    '          OOHAABBO      ',
    '          OOAAABOO      ',
    '         OOAAABBO       ',
    '         OAAABBOO       ',
    '        OOAABBBO        ',
    '        OAABBBOO        ',
    '        OABBBBO         ',
    '        OBBBBOO         ',
    '        OOBBOO          ',
    '         OOOO           ',
  ],
  star: [
    '           OOO          ',
    '           OHO          ',
    '          OOHOO         ',
    '          OHHAO         ',
    '          OHHAO         ',
    '         OOHHAOO        ',
    '         OHHAAAO        ',
    '  OOOOOOOOHAAABOOOOOOOO ',
    '  OHHHHHHHHAAAAHHAAABBO ',
    '  OOHHHHHAAAAAAAAAABBOO ',
    '   OOHHHAAAAAAAAAABBOO  ',
    '    OOAAAAAAAAAABBBOO   ',
    '     OOOHAAAAAAABOOO    ',
    '       OHAAAAAABBO      ',
    '      OOHAAAAABBBOO     ',
    '      OHHAABBBBBBBO     ',
    '      OHABBBOBBBBBO     ',
    '      OHBBOOOOOBBBO     ',
    '     OOABOO   OOBBOO    ',
    '     OAOOO     OOOBO    ',
    '     OOO         OOO    ',
    '                        ',
    '                        ',
    '                        ',
  ],
  // ---- IC2: weapon ICON silhouettes (DOM menu icons only — not baked into the 3D atlas) ----
  wic_axe: [
    '                        ',
    '                        ',
    '       OOOOOOOOOOO      ',
    '      OOLLLLLLLLAOO     ',
    '     OOLLHHHHHHHHAOO    ',
    '     OLLHHHHHHHHAAAOOO  ',
    '     OLHHHHHHHHAAAAHCO  ',
    '     OLHHHHHHHAAAAACCO  ',
    '     OHAAHHHHAAAAAACCO  ',
    '     OOOOHBHAAAAAACCOL  ',
    '        OOOHABBDDDCCL   ',
    '          OHABOOOOCLL   ',
    '          OHADO  OLL    ',
    '          OHADO         ',
    '          OHADO         ',
    '          OHADO         ',
    '          OHADL         ',
    '          OHBDL         ',
    '          OHBDL         ',
    '          OABDL         ',
    '          OABDL         ',
    '          OBDDL         ',
    '          OODLL         ',
    '           OLL          ',
  ],
  wic_gun: [
    '                        ',
    '                        ',
    '              OOOO      ',
    '              OLAO      ',
    '    OOOOOOOOOOOLBOOOOO  ',
    '    OLLLLLLLLLLHAHHHAO  ',
    '    OLHHHHHHHHHAAAACCO  ',
    '    OLHHHHHHHHAAAAACCO  ',
    '    OLHHHHHHHAAAAAACCO  ',
    '    OHAHHHHHBBBBDDDDDL  ',
    '    OOOLHHHABOOOOOOOLL  ',
    '      OLHHAEEO          ',
    '      OLHAAEEO          ',
    '      OHAAABOO          ',
    '     OOHAAADO           ',
    '     OHHAAADO           ',
    '     OHAAAADO           ',
    '     OHAAAADO           ',
    '     OHAAAADL           ',
    '     OADDDDDL           ',
    '     OOOOOLLL           ',
    '                        ',
    '                        ',
    '                        ',
  ],
  wic_ring: [
    '        OOCCCOO         ',
    '     OOOOLCCCHOOOO      ',
    '    OOLLLLCCCHLLAOO     ',
    '   OOLLLHHCCCHHHHAOO    ',
    '  OOLLLHHHHHHHHHAAAOO   ',
    ' OOLLLHHAAAABBHAAAAAOO  ',
    ' OLLLHHAAOOOOOAAAAAABO  ',
    ' OLLHHAAOO   OOAAAAADO  ',
    'OOLHHAAOO     OOAAAADLO ',
    'OLLHHAOO       OOHABBBL ',
    'OLHHHAO         OHBBBDL ',
    'OLHHHAO         OHBBBDL ',
    'OLHHHBO         OABBBDL ',
    'OHHHHBOO       OOABBDDL ',
    'OOLHHHAOO     OOAABBDLL ',
    ' OLHHAAAOO   OOAABBBDL  ',
    ' OAHAAAAAOOOOOAABBBDDL  ',
    ' OOAAAAAAHHHAAABBBDDLL  ',
    '  OOAAAAAABBBBBBBDDLL   ',
    '   OOAAAABBBBBBBDDLL    ',
    '    OOBDDBBBBDDDDLL     ',
    '     OOOLBDDDDLLLL      ',
    '        OLLLLLL         ',
    '                        ',
  ],
  wic_snow: [
    '    OOOOOOO OOOOOOO     ',
    '    OLLOLHO OLHOLAO     ',
    '    OLLLHAOOOHHLLBO     ',
    '   OOLLLAOHOHOLHHBOO    ',
    '   OLLLHHLHOLLLHAAAO    ',
    '   OHLHHHAAOHHHAABBO    ',
    '   OOLHHHAOOOHAAABOO    ',
    'OOOOOHAAHHHOHHBBBDOOOOO ',
    'OLHOLHOOHHBOHBBOOHBOHBL ',
    'OLHLLAOOOLHCHBOOOHAHADLO',
    'LLHHHHLLLHCCCAHHHHBBBBBL',
    'LHHHHHHHHCCCCCAAABBBBBDL',
    'HHHHHBBBBACCCDDDDBBBBDDL',
    'OLAAHBOOOHBCADOOOADDBDLL',
    'OHAOABOOHHDOHABOOBDLDDL ',
    'OOOOOHHHHDDOBBBAABLLLLL ',
    '   OOHAAADOOOABBBDLO    ',
    '   OHHAAAABOAABBBBDL    ',
    '   OAAAADDDOBDBBBDDL    ',
    '   OOHAADOBLBOABDDLL    ',
    '    OHDDBBLLOADDDDL     ',
    '    OBDOBDL ODDLDDL     ',
    '    OOOLLLL OLLLLLL     ',
    '                        ',
  ],
  wic_meteor: [
    '                        ',
    '     OOO                ',
    '  OOOOLOO               ',
    '  OLLOHHOO              ',
    '  OLLHOHHOO             ',
    '  OOHLHOHHOO            ',
    '   OOHHHOHHOO           ',
    '    OOHHHOHHOOOOO       ',
    '     OOHHHOLAOOAOOOO    ',
    '      OOHHLHAHHHHHBOO   ',
    '       OOAHAACAAAAABOO  ',
    '        OOHACCCAAABBBL  ',
    '         OHACCCCABBBDL  ',
    '        OOHACCCABBBBDLO ',
    '        OAHAAAABBBBBDDL ',
    '        OOHAAABBBEEBDLL ',
    '         OHAABBBBEEBDL  ',
    '         OBABBBBBEBDDL  ',
    '         OOBBBBBBBDDLL  ',
    '          OOBDDDDDDLL   ',
    '           OLLLDLLLL    ',
    '              OLL       ',
    '                        ',
    '                        ',
  ],
  wic_saw: [
    '         OOO            ',
    '         OHO    OOO     ',
    '        OOHO  OOOAO     ',
    '        OLHOOOOLAOO     ',
    '   OOO OOLHLLLLLBO      ',
    '   OHOOOLLHHHHHHBOO     ',
    '   OHLLLLHHHHHHAAAOOOOO ',
    '   OOLHHHHHHHHAAAAHHBBO ',
    '    OLHHHHHHHAAAAAADDOL ',
    '    OLHHHHCCCCCAAAADOL  ',
    '    OLHHHHCOOOCAAAADL   ',
    '  OOOLHHHHCO OCAAABDLO  ',
    '  OHLLHHHACOOOCAABBBBOO ',
    '  OOHBHHAACCCCCABBDDDDL ',
    '   OOOHAAAAAAAABBBDLLDL ',
    '     OAAAAAAAABBBDDLOLL ',
    '    OOOHAAAAABBBBDLL    ',
    '    OHAADDAABBDDDDL     ',
    '    OABOOOBBDDDLODL     ',
    '    OOOO OOADLLLODL     ',
    '          OBDL  OLL     ',
    '          OBLL          ',
    '          OLL           ',
    '                        ',
  ],
  wic_drone: [
    '                        ',
    '                        ',
    '   OOOOOO       OOOOOO  ',
    '  OOLLHHOO     OOAAAAOO ',
    '  OLLOOHHO     OAAOOAAO ',
    '  OLOLHOHO     OAOHAOAO ',
    '  OHOHHLHOO   OOHHADOBO ',
    '  OHHOLHHHOOOOOHHADOBBO ',
    '  OOHHHAHHLLHHHHBDDBBOL ',
    '   OOOOOHHCCCAABDOOOOL  ',
    '       OOLCCCAADOO      ',
    '        OHCCAAADO       ',
    '        OHAAAAADO       ',
    '       OOHAAAAADOO      ',
    '   OOOOOHABDDDDBBOOOOO  ',
    '  OOAAHHABOOOOOBBABDDOO ',
    '  OAAOHABOO   OOABDLDDL ',
    '  OAOHABDO     OBDBDODL ',
    '  OAOABOAO     ODLDDODL ',
    '  OAAOOABO     ODDOODDL ',
    '  OOAAABOO     OODDDDLL ',
    '   OOOOOO       OLLLLL  ',
    '                        ',
    '                        ',
  ],
  wic_book: [
    '                        ',
    '                        ',
    '      OOOOOOOOOOOOOOOO  ',
    '      OEELLLLLLLLLHHAO  ',
    '      OEEHHHHHHHHHACCO  ',
    '      OEEHHHHHHHHAACCO  ',
    '      OEEHHHHHHHAAACCO  ',
    '      OEEHHHHHHAAAACCO  ',
    '      OEEHHHHHAAAAACCO  ',
    '      OEEHHHHCCAAAACCO  ',
    '      OEEHHHACCAAAACCL  ',
    '      OEEHHCCCCCCAACCL  ',
    '      OEEHACCCCCCABCCL  ',
    '      OEEAAAACCAABBCCL  ',
    '      OEEAAAACCABBBCCL  ',
    '      OEEAAAAAABBBBCCL  ',
    '      OEEAAAAABBBBBCCL  ',
    '      OEEAAAABBBBBBCCL  ',
    '      OEEAAABBBBBBBCCL  ',
    '      OEEAABBBBBBBDCCL  ',
    '      OEEDDDDDDDDDDDDL  ',
    '      OOOOLLLLLLLLLLLL  ',
    '                        ',
    '                        ',
  ],
  wic_swirl: [
    '                        ',
    '         OOOOOO         ',
    '       OOOLLLHOOOO      ',
    '  OOO OOHHHAAAHAAOO     ',
    ' OOLOOOHHOOOOOOOAAOOO   ',
    ' OLHOOHHOOOLLAOOOAAAO   ',
    'OOHOOHHOOLHHBBAAOOOAOO  ',
    'OHHOLHOOHHBOOOOAAOOBBO  ',
    'OHOOHAOLHOOHHAOOAAOOBLO ',
    'OHOOHOOLBOHHAAAOOHBOBBL ',
    'OHOOHOOLBAHCBBAAOBDLOBL ',
    'OHOOHOOLBOCCCOHDOOBLOBL ',
    'OHOOLHOAAHHCDAHDOOBLODL ',
    'OHHOHBOOAAADDOHDLOBLODL ',
    'OOHOOAAOOADDOOBDOABLODL ',
    ' OHAOOAAOOOOHBBLOBDODDL ',
    ' OOAOOOAAHHBBDLOBDLODLL ',
    '  OAAAOOOBDDLLOBDLODDL  ',
    '  OOOAAOOOOOOOBDLLODLL  ',
    '    OOABBHAABBDLL OLL   ',
    '     OOOOBDDDLLL        ',
    '        OLLLLL          ',
    '                        ',
    '                        ',
  ],
  wic_caltrop: [
    '                        ',
    '                        ',
    '   OOOO           OOOO  ',
    '   OLLOO         OOHAO  ',
    '   OLLHOO       OOHABO  ',
    '   OOHLHOO     OOHABOO  ',
    '    OOHHHOO   OOHABOO   ',
    '     OOHHHOOOOOHABOO    ',
    '      OOHHHOAOHABOO     ',
    '       OOHCCHHABOO      ',
    '        OOCCCABOO       ',
    '        OAHCAADAO       ',
    '        OOHAAADOO       ',
    '       OOHABDDABOO      ',
    '      OOHABOAOBBBOO     ',
    '     OOHABOOOOOBBBOO    ',
    '    OOHABOO   OOBBDOO   ',
    '   OOHABOO     OODBDOO  ',
    '   OHABOO       OODDDL  ',
    '   OABOO         OODDL  ',
    '   OOOO           OLLL  ',
    '                        ',
    '                        ',
    '                        ',
  ],
  wic_ball: [
    '        OOOOOOO         ',
    '     OOOOLLLLHOOOO      ',
    '    OOLLLLHHHHLLAOO     ',
    '   OOLLLHHHHHHHHHAOO    ',
    '  OOLLLHHHHHHHHHAAAOO   ',
    ' OOLLLHHHHHHHHHAAAAAOO  ',
    ' OLLLHHCCCHHHHAAAAAABO  ',
    ' OLLHHCCCCHHHAAAAAAADO  ',
    'OOLHHHCCCCHHAAAAAAAADLO ',
    'OLLHHHHCCCHAAAAAAAABBBL ',
    'OLHHHHHHHHAAAAAAAABBBDL ',
    'OLHHHHHHHAAAAAAAABBBBDL ',
    'OLHHHHHHAAAAAAAABBBBBDL ',
    'OHHHHHHAAAAAAAABBBBBDDL ',
    'OOLHHHAAAAAAAABBBBBBDLL ',
    ' OLHHAAAAAAAABBBBBBBDL  ',
    ' OAHAAAAAAAABBBBBBBDDL  ',
    ' OOAAAAAAAABBBBBBBDDLL  ',
    '  OOAAAAAABBBBBBBDDLL   ',
    '   OOAAAABBBBBBBDDLL    ',
    '    OOBDDBBBBDDDDLL     ',
    '     OOOLBDDDDLLLL      ',
    '        OLLLLLL         ',
    '                        ',
  ],
  wic_hole: [
    '          OOO           ',
    '      OOOOOHOOOOO       ',
    '    OOOLLLLLLLLHOOO     ',
    '   OOLLLHHHHHHHHHAOO    ',
    '  OOLLLHHHHHHHHHAAAOO   ',
    '  OLLLHHHHHHHHHAAAAAO   ',
    ' OOLLHHHACCCCCAAAAADOO  ',
    ' OLLHHHACOOOOOCAAAAABO  ',
    ' OLHHHACOO   OOCAAAADL  ',
    ' OLHHHCOO     OOCAABDL  ',
    'OOLHHHCO       OCABBDLO ',
    'OHLHHHCO       OCBBBDBL ',
    'OOLHHHCO       OCBBBDLL ',
    ' OLHHHCOO     OOCBBBDL  ',
    ' OLHHHACOO   OOCABBBDL  ',
    ' OHHHAAACOOOOOCABBBDDL  ',
    ' OOHAAAAACCCCCABBBBDLL  ',
    '  OAAAAAAAABBBBBBBDDL   ',
    '  OOAAAAAABBBBBBBDDLL   ',
    '   OOADAABBBBBBDDDLL    ',
    '    OOOBDDDDDDDDLLL     ',
    '      OOLLLBLLLLL       ',
    '          OLL           ',
    '                        ',
  ],
  wic_rail: [
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '      OOOOOOOOOOO       ',
    '      OLLLHOLLLAO       ',
    '      OLHHAOLHHBO OOOOO ',
    '      OLHHAOLHABO OCCCO ',
    '  OOOOOLHHBOHAABOOOCCCL ',
    '  OLLLLLHHHHHAAAHHHCCCL ',
    '  OLHHHHHHHAAAAAAAACCCL ',
    '  OLHHHHHHAAAAAAAABCCCL ',
    '  OLHHHHHAAAAAAAABBCCCL ',
    '  OHABBHAABBAAADDDDCCCL ',
    '  OOOOOHAABOHAADLLOCCCL ',
    '      OHAADOHABDL OCCCL ',
    '      OHAADOHBBDL OLLLL ',
    '      OADDDOBDDDL       ',
    '      OOOOOOLLLLL       ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
  ],
  wic_spore: [
    '                        ',
    '                        ',
    '                        ',
    '            OOOOOO      ',
    '           OOLLLAOO     ',
    '        OOOOLLHHHAOOO   ',
    '       OOLLLLHHHAAHAO   ',
    '      OOLLHHHHHAAAADOO  ',
    '      OLLHHHHHAAAAAABO  ',
    '      OLHHEEHAAAEEAADO  ',
    '      OLHHEEAAAAAAADDL  ',
    '      OHHHHAAAAAAAADLL  ',
    '      OOAHAAAAAAADDDL   ',
    '       OOAAAAAAAADLLL   ',
    '        OOHAAEEABDLO    ',
    '        OAHAAEEBBDBL    ',
    '        OOHAAEEBBDLL    ',
    '         OAAABBBDDL     ',
    '         OOBBBBDDLL     ',
    '          OOBDDDLL      ',
    '           OLLLLL       ',
    '                        ',
    '                        ',
    '                        ',
  ],
  wic_sun: [
    '          OOO           ',
    '          OHO           ',
    '          OHO           ',
    '      OOO OHO OOO       ',
    '      OHOOOHOOOAO       ',
    '      OHHOOOOOAAO       ',
    '   OOOOOOLLLLAOOOOOO    ',
    '   OHHOOLLHHHAAOOAAO    ',
    '   OOHOLCCCHAAAAOAOO    ',
    '    OOLLCCCAAAAAAOO     ',
    'OOOOOOLHCCCAAAAADOOOOOO ',
    'OHHHHOLHHAAAAAAADOBBBBL ',
    'OOOOOOLHAAAAAAAADLLLLLL ',
    '    OOAAAAAAAAADDLO     ',
    '   OOAOAAAAAAADDLBOO    ',
    '   OAAOOAAAAADDLLBDL    ',
    '   OOOOOOADDDDLLOLLL    ',
    '      OAAOOOLLBBL       ',
    '      OAOOOBLOODL       ',
    '      OOO OBL OLL       ',
    '          OBL           ',
    '          OBL           ',
    '          OLL           ',
    '                        ',
  ],
  wic_prism: [
    '                        ',
    '           OOO          ',
    '           OHO          ',
    '           OHO          ',
    '          OOHOO         ',
    '          OLCHO         ',
    '         OOCCBOO        ',
    '         OLCCHAO        ',
    '        OOLCHABOO       ',
    '        OLCCAAAAO       ',
    '       OOLCAAAADOO      ',
    '       OLCCAAAAABO      ',
    '      OOHCCAAAAADOO     ',
    '      OHHCAAAAAABBL     ',
    '     OOHCCAAAAABBDLO    ',
    '     OHHCAAAAABBBBBL    ',
    '    OOHCCAAAABBBBBDLO   ',
    '    OHHCCAAABBBBBBBDL   ',
    '   OOHACAAABBBBBBBDDLO  ',
    '   OAADDDDDDDDDDDDDDDL  ',
    '   OOOOOOOLLBLLLLLLLLL  ',
    '           OLL          ',
    '                        ',
    '                        ',
  ],
  wic_beam: [
    '                        ',
    '                 OOOOOO ',
    '                OOHHHAO ',
    '               OOHHCCDO ',
    '              OOHHCCCDO ',
    '             OOHHCCCDDO ',
    '            OOHHCCCDDOO ',
    '           OOHHCCCDDOO  ',
    '          OOHHCCCDDOO   ',
    '         OOHHCCCDDOO    ',
    '        OOHHCCCDDOO     ',
    '       OOHHCCCDDOO      ',
    '      OOHHCCCDDOO       ',
    '     OOHHCCCDDOO        ',
    '    OOHHCCCDDOO         ',
    '   OOHHCCCDDOO          ',
    '  OOHHCCCDDOO           ',
    ' OOHHCCCDDOO            ',
    ' OHHCCCDDOO             ',
    ' OAACCDDOO              ',
    ' OOAADDOO               ',
    '  OOADOO                ',
    '   OOOO                 ',
    '                        ',
  ],
  wic_drop: [
    '                        ',
    '                        ',
    '            OOO         ',
    '            OHO         ',
    '           OOHOO        ',
    '          OOLLHOO       ',
    '          OLLHHAO       ',
    '         OOLHHHBOO      ',
    '        OOLLHHAAAOO     ',
    '        OLLHHAAAAAO     ',
    '       OOLHHAAAAADOO    ',
    '       OLLHAAAAAAABO    ',
    '      OOLHCCAAAAAADLO   ',
    '     OOLHACCCAAAABBBOO  ',
    '     OAHAACCCAAABBBDBL  ',
    '     OOHAACCAAABBBBDLL  ',
    '      OAAAAAAABBBBDDL   ',
    '      OOHAAAABBBBBDLL   ',
    '       OAAAABBBBBDDL    ',
    '       OOBABBBBBDDLL    ',
    '        OOBDDDDDDLL     ',
    '         OLLLLLLLL      ',
    '                        ',
    '                        ',
  ],
  wic_shock: [
    '    OOOLLHHHHLLHOOO     ',
    '   OOLLHAAOOOHAALAOO    ',
    '  OOLLHOOOOOOOOOABAOO   ',
    ' OOLLOOOOLLLLHOOOOAAOO  ',
    'OOLLOOOHHHAAABAAOOOAAOO ',
    'OLLOOOHHOOOOOOOAAOOOHBO ',
    'OLHOOHHOOOOHOOOOAAOOBDOO',
    'LHOOHHOOLHHAAHAOOAAOOHBL',
    'LAOOHOOLHBOOOAAAOOBOOADL',
    'HAOLHOOHBOOCOOADOOHBOBDL',
    'HOOLAOOHOOCCCOOAOOHDLOBL',
    'HOOLAOHAOCCCCCOBBOADLODL',
    'HOOLAOOAOOCCCOOBOOADLODL',
    'LHOHBOOHAOOCOOHBLOBDOADL',
    'LAOOAOOAAAOOOHBDLOBLOADL',
    'HAOOAAOOADABBBDLOBDLODDL',
    'OLAOOAAOOOOBOLLOBDLOBDLL',
    'OABOOOAAOOOOOOOBDLOODDL ',
    'OOAAOOOABHHAABBDLOODDLL ',
    ' OOAAOOOOBDDDDLLOODDLL  ',
    '  OOAHBOOOLLLOOOBDDLL   ',
    '   OOBDHABOOOAADDDLL    ',
    '    OOOBDDBDDDDDLLL     ',
    '      OLLLLLLLLLL       ',
  ],
  wic_whip: [
    '                        ',
    '             OOOOOO     ',
    '   OOOOOO    OLHHAO     ',
    '   OCCCCO   OOHBOAOO    ',
    '   OCCCCO   OLHOOAAO    ',
    '   OCCCCO  OOHBOOOAO    ',
    '   OCCCCO  OLAOO OAOO   ',
    '   OCCCCO OOABO  OOAO   ',
    '   OCCCCO OLAOO   OBO   ',
    '   OCCCCOOOHBO    OBOO  ',
    '   OOHHAOOHABO    OOBL  ',
    '    OOLHAOHBOO     OBL  ',
    '     OHHHHABO      OBL  ',
    '     OOAABBOO      OLL  ',
    '      OOABOO            ',
    '       OOOO             ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
  ],
  wic_burst: [
    '          OOO           ',
    '          OHO           ',
    '          OHO           ',
    '   OOO    OHO    OOO    ',
    '   OHOO   OHO   OOAO    ',
    '   OOHOO  OHO  OOAOO    ',
    '    OOHOO OHO OOAOO     ',
    '     OOHOOOAOOOAOO      ',
    '      OOHOOAOOAOO       ',
    '       OOLHHHAOO        ',
    'OOOOOOOOOHCCCBOOOOOOOOO ',
    'OHHHHHHAAHCCCDABBBBBBBL ',
    'OOOOOOOOOHCCCDOOOLLLLLL ',
    '       OOABDDDOO        ',
    '      OOAOOAOOBOO       ',
    '     OOAOOOBOOOBOO      ',
    '    OOAOO OBO OOBOO     ',
    '   OOAOO  OBL  OODOO    ',
    '   OAOO   OBL   OODL    ',
    '   OOO    OBL    OLL    ',
    '          OBL           ',
    '          OBL           ',
    '          OLL           ',
    '                        ',
  ],
  // ---- IC3: item / tome / meta ICON silhouettes (DOM menu icons only) ----
  iic_potion: [
    '                        ',
    '         OOOOOOOOO      ',
    '         OCCCCCCCO      ',
    '         OCCCCCCCO      ',
    '         OCCCCCCCO      ',
    '         OOLHHHBOO      ',
    '          OLHHHBO       ',
    '          OLHHHBO       ',
    '         OOLHHABOO      ',
    '        OOLLHAAAAOO     ',
    '       OOLLHAAAAAAOO    ',
    '      OOLLCCAAAAAABOO   ',
    '      OLLCCCCAAAAABBL   ',
    '      OLHCCCCAAAABBDL   ',
    '      OHAACCAAAABBBDL   ',
    '      OHAAAAAAABBBBDL   ',
    '      OHAAAAAABBBBBDL   ',
    '      OEEEEEEEEEEEEEL   ',
    '      OOEEEEEEEEEEELL   ',
    '       OOBABBBBBDDLL    ',
    '        OOBDBBDDDLL     ',
    '         OLLBDDLLL      ',
    '           OLLLL        ',
    '                        ',
  ],
  iic_boot: [
    '                        ',
    '                        ',
    '       OOOOOOO          ',
    '       OLLLLHO          ',
    '       OLHHHAO          ',
    '       OLCCCCO          ',
    '       OLCCCCO          ',
    '       OLHHHBO          ',
    '       OLCCCCO          ',
    '       OLHHHBO          ',
    '       OLCCCCO          ',
    '       OLCCCCO          ',
    '       OHAAABOOOOOOOO   ',
    '       OHAAAAHHHHAABOO  ',
    '       OHAAAAAABBBBBDL  ',
    '       OHAAAAABBBBBBDL  ',
    '       OHAAAABBBBBBBDL  ',
    '       OHAAABBBBBBBDDL  ',
    '       OEEEEEEEEEEEEEL  ',
    '       OEEEEEEEEEEEEEL  ',
    '       OOOLLLLLLLLLLLL  ',
    '                        ',
    '                        ',
    '                        ',
  ],
  iic_shield: [
    '                        ',
    '                        ',
    '      OOOOOOOOOOOOOOO   ',
    '      OLLLLLLLLLLLHAO   ',
    '      OLLHHHHHHHHHABO   ',
    '      OLHHHHCCCHHAABO   ',
    '      OLHHHHCCCHAAABO   ',
    '      OLHHHHCCCAAAADO   ',
    '      OLCCCCEEECCCCDO   ',
    '      OLCCCCEEECCCCDO   ',
    '      OLCCCCEEECCCCDO   ',
    '      OHHHHACCCAAADDL   ',
    '      OOAHAACCCAADDLL   ',
    '       OOHAACCCAADLL    ',
    '        OAAACCCADDL     ',
    '        OOAACCCDDLL     ',
    '         OOHCCCDLL      ',
    '          OBABDDL       ',
    '          OOBDDLL       ',
    '           OOBLL        ',
    '            OBL         ',
    '            OLL         ',
    '                        ',
    '                        ',
  ],
  iic_clover: [
    '                        ',
    '                        ',
    '          OOOOO         ',
    '         OOLLHOO        ',
    '        OOLLHHHOO       ',
    '        OLLHHHHAO       ',
    '        OLHHHHHBO       ',
    '     OOOOHHHHHBBOOOO    ',
    '    OOLLHOHHHBBOHHAOO   ',
    '   OOLLHHHOHABOHHAABOO  ',
    '   OLLHHHHHHCAHHAAABBL  ',
    '   OLHHHHHACCCAAAABBDL  ',
    '   OHHHHHBBACDDAABBDDL  ',
    '   OOHHHBBOHADOBBBDDLL  ',
    '    OOABBOHHAABOBDDLL   ',
    '     OOOOHHAAABBLLLL    ',
    '        OHAAABBDL       ',
    '        OAAABBDDL       ',
    '        OOBBBBDLL       ',
    '         OOBDDDLO       ',
    '          OLLOADL       ',
    '             ODDL       ',
    '             OLLL       ',
    '                        ',
  ],
  iic_gear: [
    '         OOOOO          ',
    '         OLLHO          ',
    '    OOO  OLHAO  OOO     ',
    '   OOHOOOOLHAOOOOAOO    ',
    '  OOLLHOOLLHHHOOHHAOO   ',
    '  OHLLHLLLHHHHHHHABAO   ',
    '  OOHHHHHHHHHHAAABDOO   ',
    '   OOLHHHHHHHAAAADOO    ',
    '   OOLHHHCCCCCAAADOO    ',
    'OOOOLLHHCCOOOCCAAABOOOO ',
    'OLLLLHHHCOO OOCAAABAABL ',
    'OLHHHHHHCO   OCAABBBBDL ',
    'OHAAHHHHCOO OOCABBDDDDL ',
    'OOOOHHHACCOOOCCBBDDLLLL ',
    '   OOHAAACCCCCBBBDLL    ',
    '   OOHAAAAAAABBBBDLO    ',
    '  OOHHAAAAAABBBBBBDOO   ',
    '  OAHABDDAABBDDDBBDDL   ',
    '  OOABDOOBBBDDLLDDDLL   ',
    '   OOAOOOOABDLLOODLL    ',
    '    OOO  OABDL  OLL     ',
    '         OBDDL          ',
    '         OLLLL          ',
    '                        ',
  ],
  iic_fang: [
    '                        ',
    '                        ',
    '                        ',
    '         OOOOOOOOOOO    ',
    '         OLLLLLLLLAO    ',
    '         OHHHHHHHHBO    ',
    '         OOCCCHHHBBO    ',
    '          OCCCHHABOO    ',
    '          OLHHHAABO     ',
    '          OHHHAAABO     ',
    '          OOHAAAADO     ',
    '           OHAAADDO     ',
    '           OHAAADOO     ',
    '           OAAAADO      ',
    '           OOHADDO      ',
    '            OHADOL      ',
    '            OBADL       ',
    '            OOBDL       ',
    '             OBLL       ',
    '             OBL        ',
    '             OLL        ',
    '                        ',
    '                        ',
    '                        ',
  ],
  iic_scroll: [
    '                        ',
    '                        ',
    '    OOOOOOOOOOOOOOOOOO  ',
    '    OCCCCCCCCCCCCCCCCO  ',
    '    OCCCCCCCCCCCCCCCCO  ',
    '    OCCCCCCCCCCCCCCCCO  ',
    '    OOOLHHHHHHHAAABOOO  ',
    '      OLHHHHHHAAAADO    ',
    '      OLHEEEEEEEEADO    ',
    '      OLHEEEEEEEEADO    ',
    '      OLHHHAAAAAAADO    ',
    '      OLHEEEEEEEEADL    ',
    '      OLHEEEEEEEEBDL    ',
    '      OHAAAAAAAABBDL    ',
    '      OHAEEEEEEEEBDL    ',
    '      OHAEEEEEEEEBDL    ',
    '      OHAAAAABBBBBDL    ',
    '    OOOHAAAABBBBBBDOOO  ',
    '    OCCCCCCCCCCCCCCCCL  ',
    '    OCCCCCCCCCCCCCCCCL  ',
    '    OCCCCCCCCCCCCCCCCL  ',
    '    OOOOOLLLLLLLLLLLLL  ',
    '                        ',
    '                        ',
  ],
  iic_wing: [
    '                        ',
    '             OOO        ',
    '             OHO        ',
    '            OOHOO       ',
    '          OOOLEHOOO     ',
    '          OEELEHEEO     ',
    '          OOEEEEEOO     ',
    '         OOLLHEHAAOO    ',
    '         OLEEHEAEEAO    ',
    '         OLHEEEEEADO    ',
    '         OLHHAEAAADO    ',
    '         OLEEAEAEEDO    ',
    '         OHAEEEEEDDO    ',
    '         OAAAAEAADOL    ',
    '         OOEEAEAEEL     ',
    '          OHEEEEEDL     ',
    '          OHAAEBBDL     ',
    '          OAAAEBDDL     ',
    '          OOBBEDDLL     ',
    '           OOBEDLL      ',
    '            OOBLL       ',
    '             OLL        ',
    '                        ',
    '                        ',
  ],
  iic_fist: [
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '       OOOOOOOOOOOOOO   ',
    '       OLLLLLLLHHHHAO   ',
    '      OOLHHHHHHAAAADO   ',
    '      OLLHHHHHAAAAADO   ',
    '      OLHHHHHAAAAAADO   ',
    '    OOOLHCCHAAAAAAADO   ',
    '    OLLLHCCAAAAAAAADL   ',
    '    OLHHHHAAAAAAAABDL   ',
    '    OLHHHAAAAAAAABBDL   ',
    '    OHBHAAAAAAAABBBDL   ',
    '    OOOHAAAAAAABBBBDL   ',
    '      OHAAAAAABBBBBDL   ',
    '      OABCCCCCCCCCCDL   ',
    '      OOOCCCCCCCCCCLL   ',
    '        OCCCCCCCCCCL    ',
    '        OOOLLLLLLLLL    ',
    '                        ',
    '                        ',
    '                        ',
  ],
  iic_target: [
    '          OHO           ',
    '       OOOOHOOOO        ',
    '     OOOHHHHHHHOOO      ',
    '    OOHHHOOHOOHAAOO     ',
    '   OOHHOOOOOOOOOAAOO    ',
    '  OOHHOOOOOOOOOOOAAOO   ',
    '  OHHOOOOHHHAAOOOOAAO   ',
    ' OOHOOOOHHOOOAAOOOOBOO  ',
    ' OHHOOOHHOO OOAAOOOBBL  ',
    ' OHOOOHHOOOOOOOAAOOOBL  ',
    'OOHOOOHOOOCCCOOOBOOOBOOO',
    'HHHHOOHO OCCCO OBOOBBBDL',
    'OOHOOOAOOOCCCOOOBLOOBLLL',
    ' OHOOOAAOOOOOOOBBLOODL  ',
    ' OHHOOOAAOO OOBBLLODDL  ',
    ' OOAOOOOAAOOOBBLLOODLL  ',
    '  OAAOOOOABBBBLLOODDL   ',
    '  OOAAOOOOOOLLLOODDLL   ',
    '   OOAAOOOOOOOOODDLL    ',
    '    OOABBOOBOODDDLL     ',
    '     OOOBBBBBDDLLL      ',
    '       OLLOBLLLL        ',
    '          ODL           ',
    '          OLL           ',
  ],
  iic_lens: [
    '                        ',
    '                        ',
    '                        ',
    '      OOOOOOOO          ',
    '    OOOLLLLLHOO         ',
    '    OLLLHHHHHAOO        ',
    '   OOLHHCCCHHHAOO       ',
    '   OLLHCCCCCCAAAO       ',
    '   OLHCCCCCCCAABO       ',
    '   OLHCCCCCCCAADO       ',
    '   OLHCCCCCCCAADO       ',
    '   OLHHCCCCCCADDO       ',
    '   OHHHCCCCCAADOO       ',
    '   OOAHAAAAAAAABOO      ',
    '    OOAAAAADDABBBOO     ',
    '     OOABDDDOBBBBBOO    ',
    '      OOOOOOOOABBBDOO   ',
    '             OBBBBDDOO  ',
    '             OODBDDDDOO ',
    '              OODDDDDDL ',
    '               OODDDDDL ',
    '                OODDDDL ',
    '                 OLLLLL ',
    '                        ',
  ],
  iic_anchor: [
    '          OOOOO         ',
    '         OOLLHOO        ',
    '         OHHAAHO        ',
    '         OHOOOHO        ',
    '         OLLLLHO        ',
    '         OHHHBBO        ',
    '       OOOOLHBOOOO      ',
    '       OLLLLHHHHAO      ',
    '       OHABHHBBBBO      ',
    '       OOOOHABOOOO      ',
    '          OHABO         ',
    '          OHABO         ',
    '          OHADO         ',
    '    OOOOO OHADO OOOOO   ',
    '    OLHAOOOHADOOOAABL   ',
    '   OOABAAOOHADOOABDDLO  ',
    '   OHAOABHHHABABBDLADL  ',
    '   OABOOOAAABBDDLLLDDL  ',
    '   OOOO OOBBBDDLL OLLL  ',
    '         OOBDDLL        ',
    '          OLLLL         ',
    '                        ',
    '                        ',
    '                        ',
  ],
  cic_skull: [
    '                        ',
    '          OOOOOOO       ',
    '        OOOLLLLHOOO     ',
    '       OOLLLHHHHLAOO    ',
    '      OOLLHHHHHHHHAOO   ',
    '      OLLHHHHHHHHAAAO   ',
    '      OLAAAAHHBBBBABO   ',
    '      OHAOOOHBBOOOADO   ',
    '      OHOO OOAOO OOAO   ',
    '      OHOO OOAOO OOBO   ',
    '      OLHOOOAAAOOOHBO   ',
    '      OLHLHHAOHHHHHDL   ',
    '      OHHHAABOHAAADDL   ',
    '      OOAAAAAHHAADDLL   ',
    '       OOHBBDDDDBDLL    ',
    '        OHBOAOBOADL     ',
    '        OADOBOBOBDL     ',
    '        OOAOBOBOBLL     ',
    '         OBOBOBOBL      ',
    '         OBOBLBLDL      ',
    '         OOLLLLLLL      ',
    '                        ',
    '                        ',
    '                        ',
  ],
  cic_spark: [
    '          OOO           ',
    '          OHO           ',
    '          OHO           ',
    '          OHO           ',
    '         OOHOO          ',
    '         OLLHO          ',
    '         OLHBO          ',
    '         OLHBO          ',
    '         OLHBO          ',
    '    OOOOOOHCBOOOOOO     ',
    'OOOOOLLLLHCCCHHHHBOOOOO ',
    'OHHHHLHHHCCCCCAAADBBBBL ',
    'OOOOOHBBBBCCCDDDDDLLLLL ',
    '    OOOOOOHCDOOOLLL     ',
    '         OHADO          ',
    '         OHADO          ',
    '         OHADL          ',
    '         OBDDL          ',
    '         OOBLL          ',
    '          OBL           ',
    '          OBL           ',
    '          OBL           ',
    '          OLL           ',
    '                        ',
  ],
  cic_lock: [
    '                        ',
    '                        ',
    '                        ',
    '           OOOOOOO      ',
    '          OOLLLLHOO     ',
    '         OOHHAABBAOO    ',
    '         OLHOOOOOHAO    ',
    '         OLAO   OHBO    ',
    '        OOLAOOOOOHBOO   ',
    '        OLLHLHHHHHAAO   ',
    '        OLHHHAAAAAADO   ',
    '        OLHHABBAAAADO   ',
    '        OLHABBOAAAADL   ',
    '        OHAABOOOHABDL   ',
    '        OHAAAAOHHBBDL   ',
    '        OHAAADOHBBBDL   ',
    '        OHAAAAHHBBBDL   ',
    '        OHAAAABBBBBDL   ',
    '        OHAAABBBBBBDL   ',
    '        OHAABBBBBBBDL   ',
    '        OBDDDDDDDDDDL   ',
    '        OOOLLLLLLLLLL   ',
    '                        ',
    '                        ',
  ],
  cic_flame: [
    '                        ',
    '                        ',
    '            OOO         ',
    '            OHO         ',
    '           OOHOO        ',
    '          OOLLHOO       ',
    '          OLLHHAO       ',
    '          OLHHHBO       ',
    '          OLHCABO       ',
    '         OOLHCABOO      ',
    '        OOLHACAAAOO     ',
    '       OOLHACCCAABOO    ',
    '       OLHAACCCAAABL    ',
    '      OOHAAACCCAABDLO   ',
    '     OOHHAACCCCCBBBBOO  ',
    '     OAAAACCCCCCCBDDDL  ',
    '     OOOHACCCCCCCBDLLL  ',
    '       OHAACCCCCBBDL    ',
    '       OAAAACCCBBDDL    ',
    '       OOBABBBBBDDLL    ',
    '        OOBDDDDDDLL     ',
    '         OLLLBLLLL      ',
    '            OLL         ',
    '                        ',
  ],
  cic_heal: [
    '                        ',
    '                        ',
    '                        ',
    '          OOOOOOO       ',
    '          OLLLLHO       ',
    '          OLHHHBO       ',
    '          OLHHHBO       ',
    '          OLHHHBO       ',
    '      OOOOOLHHABOOOOO   ',
    '      OLLLLLHAAAHHHBO   ',
    '      OLHHHHCCCAAAADO   ',
    '      OLHHHACCCAAAADL   ',
    '      OLHHAACCCAAABDL   ',
    '      OABBBAAAADDDDDL   ',
    '      OOOOOHAAADOLLLL   ',
    '          OHAAADL       ',
    '          OHAABDL       ',
    '          OHABBDL       ',
    '          OBDDDDL       ',
    '          OOLLLLL       ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
  ],
  cic_play: [
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '        OOOO            ',
    '        OLHOOO          ',
    '        OLHLHOO         ',
    '        OLHHHAOOO       ',
    '        OLHHHHHAOO      ',
    '        OLHHHAAAAOOO    ',
    '        OLHHAAAAAHBOOO  ',
    '        OLHAAAAAAAAHBL  ',
    '        OHAAAAAAAADDDL  ',
    '        OHAAAAAADDDLLL  ',
    '        OHAAAAADDLLL    ',
    '        OHAAADDDLL      ',
    '        OHAADDOLL       ',
    '        OHDDDOL         ',
    '        OADOOL          ',
    '        OOOO            ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
  ],
  cic_store: [
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '     OOOOOOOOOOOOOOOO   ',
    '     OCCCCCCCCCCCCCCO   ',
    '     OCCCCCCCCCCCCCCO   ',
    '    OOCCCCCCCCCCCCCCOO  ',
    '    OCCCCCCCCCCCCCCCCL  ',
    '    OCCCCCCCCCCCCCCCCL  ',
    '    OOCCCACCCCCCCCCCLL  ',
    '     OLHAABBDDDABBBDL   ',
    '     OHAAABOOOOHBBBDL   ',
    '     OHAAADO  OABBBDL   ',
    '     OHAAADO  OABBBDL   ',
    '     OHAAADO  OABBBDL   ',
    '     OHAAADO  OABBDDL   ',
    '     OHAAADL  OABDDDL   ',
    '     OADDDDL  ODDDDDL   ',
    '     OOOOLLL  OLLLLLL   ',
    '                        ',
    '                        ',
  ],
  cic_chart: [
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                OOOOOO  ',
    '                OCCCCO  ',
    '                OCCCCO  ',
    '                OCCCCO  ',
    '          OOOOOOOCCCCO  ',
    '          OHHHAOOCCCCL  ',
    '          OHAABOOCCCCL  ',
    '          OHAADOOCCCCL  ',
    '    OOOOOOOHAADOOCCCCL  ',
    '    OLLHAOOHAADOOCCCCL  ',
    '    OLHABOOHAADOOCCCCL  ',
    '    OHAABOOHAADLOCCCCL  ',
    '    OHAABOOHABDLOCCCCL  ',
    '    OHAADOOHBBDLOCCCCL  ',
    '    OHAADOOHBBDLOCCCCL  ',
    '   OOHAADOOABBDOOCCCCLO ',
    '   OAADDDBBBDDDDDDDDDDL ',
    '   OOOOOOLLLLLLLLLLLLLL ',
    '                        ',
    '                        ',
  ],
  cic_trophy: [
    '                        ',
    '                        ',
    '                        ',
    '    OOOOOOOOOOOOOOOOOO  ',
    '   OOHHCCLLLLLLLHHAAAOO ',
    '   OHHOCCHHHHHHHABBOAAO ',
    '   OHOOOLHHHHHHAABOOOBO ',
    '   OHOOOLHHHHHAAABOOOBO ',
    '   OHHOOLHHHHAAAADOOBBL ',
    '   OOHHHHHHHAAAADDBBBLL ',
    '    OOOOOABAABBDDOOOLL  ',
    '        OOOHABOOOO      ',
    '          OHADO         ',
    '          OHADO         ',
    '          OHADO         ',
    '       OOOOHADOOOOO     ',
    '       OHHHHABAAABL     ',
    '       OHAAABBBBBDL     ',
    '       OADDDDDDDDDL     ',
    '       OOOOLLLLLLLL     ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
  ],
  cic_globe: [
    '                        ',
    '          OOOOO         ',
    '       OOOOEEEOOOO      ',
    '      OOLLELHHELAOO     ',
    '     OOLLHEHHHEHHAOO    ',
    '    OOLLHHHHHHHHAAAOO   ',
    '   OOLLHHEHHHHHEAAAAOO  ',
    '   OLLHHCCCCHHAEAAAABO  ',
    '   OLHHHCCCCHAAEAAAADO  ',
    '  OOLHHHCCCCAAAAAAAADLO ',
    '  OLLHHHCCCCAAAAAAABBBL ',
    '  OEEEEEEEEEEEEEEEEEEEL ',
    '  OHHHHHHAAAAACCABBBDDL ',
    '  OOLHHHAAAAACCCCBBBDLL ',
    '   OLHHAAEAAACCCCBBBDL  ',
    '   OAHAAAEAAAACCBBBDDL  ',
    '   OOAAAAEAAABBEBBDDLL  ',
    '    OOAAAAAABBBBBDDLL   ',
    '     OOAAAEBBBEBDDLL    ',
    '      OOBDEBBDEDDLL     ',
    '       OOOLEEELLLL      ',
    '          OLLLL         ',
    '                        ',
    '                        ',
  ],
  cic_banish: [
    '                        ',
    '           OOO          ',
    '       OOOOOHOOOOO      ',
    '      OOLLLLLLLLAOOO    ',
    '     OOLLAAAAABHHCCOO   ',
    '    OOLLAAOOOOOHCCCCO   ',
    '   OOLLAAOO  OOCCCCCOO  ',
    '   OLLAAOO  OOCCCCCABO  ',
    '   OLAAOO  OOCCCCCDADO  ',
    '   OLAOO  OOCCCCCOOHDL  ',
    '  OOLAO  OOCCCCCOOOHDLO ',
    '  OHLAO OOCCCCCOO OADBL ',
    '  OOLAOOOCCCCCOO  OADLL ',
    '   OLBOOCCCCCOO  OOADL  ',
    '   OLHHCCCCCOO  OOAADL  ',
    '   OAHCCCCCOO  OOAADDL  ',
    '   OOCCCCCOO  OOAADDLL  ',
    '    OCCCCDOOOOOAADDLL   ',
    '    OOCCAAHHAAAADDLL    ',
    '     OOOBDDDDDDDDLL     ',
    '       OOOLLBLLLLL      ',
    '           OLL          ',
    '                        ',
    '                        ',
  ],
  cic_pin: [
    '                        ',
    '                        ',
    '            OOOOO       ',
    '          OOOLLHOOO     ',
    '         OOLLLHHLAOO    ',
    '        OOLLHHHHHHAOO   ',
    '        OLLHHHHHHAAAO   ',
    '        OLHHHCCCAAABO   ',
    '        OLHHCCCCCAADO   ',
    '        OLHHCCCCCAADO   ',
    '        OLHHHCCCAAADO   ',
    '        OHHHAAAAAADDO   ',
    '        OOAAAAAAADDOL   ',
    '         OOHAAAAADOL    ',
    '          OAAAAADDL     ',
    '          OOHAAADLL     ',
    '           OAAADDL      ',
    '           OOHBDLL      ',
    '            OBDDL       ',
    '            OOBLL       ',
    '             OBL        ',
    '             OLL        ',
    '                        ',
    '                        ',
  ],
  cic_check: [
    '                        ',
    '                        ',
    '                        ',
    '                  OOOOO ',
    '                 OOHHAO ',
    '                OOHHADO ',
    '                OHHADDO ',
    '               OOHADDOO ',
    '              OOHHADOO  ',
    '             OOHHADDO   ',
    '   OOOOO    OOHHADDOL   ',
    '   OLLHOO   OHHADDOL    ',
    '   OLHHAOO OOHADDOL     ',
    '   OHHHHAOOOHHADOL      ',
    '   OOAHAAAOHHADDL       ',
    '    OOAAAAHHADDLL       ',
    '     OOAAAAADDLL        ',
    '      OOAAADDLL         ',
    '       OOBDDLL          ',
    '        OOBLL           ',
    '         OLL            ',
    '                        ',
    '                        ',
    '                        ',
  ],
  cic_gamepad: [
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '       OOOOOOOOOOO      ',
    '    OOOOLLLLHHHHAOOOO   ',
    '   OOLHHHHHHAAAAAHHBOO  ',
    '  OOLLAOOLHAAAAACCABBOO ',
    '  OLLAAOOAAAAAAACCBBBBL ',
    '  OLHAOOOOHAAAAAABBCBDL ',
    '  OHHHAOOHHAAAAABBCCCDL ',
    '  OOHHBOOHAAAAABBBBCDLL ',
    '   OOABAHHAAAABBDDDDLL  ',
    '    OOOOADDDDDDDDLLLL   ',
    '       OOOOOOLLLLL      ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
  ],
  cic_clock: [
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '         OOOOOOOOO      ',
    '        OOHLHOLLAO      ',
    '       OOHOLHCLHBOOO    ',
    '      OOHLLLHCHAAHAO    ',
    '      OHOLHHHCAAAADOO   ',
    '      OLLLHHHCAAAAABO   ',
    '      OLHHHHACAAAAADO   ',
    '      OHHHHACCCCCCADL   ',
    '      OOLHAAACAAAABDL   ',
    '      OLHAAAAAAAABBDL   ',
    '      OABAAAAAAABBDDL   ',
    '      OOOHAAAAABBDDLL   ',
    '        OADAAABBDDLL    ',
    '        OOOBDDDDDLL     ',
    '          OOOLLLLL      ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
  ],
  cic_crown: [
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '            OOO         ',
    '           OOCOO        ',
    '           OCCCO        ',
    '     OOO   OLCBO   OOO  ',
    '     OHOO OOLHBOO OOBO  ',
    '     OLHOOOLHAAAOOOHBO  ',
    '     OLHHOOHAAABOOHHDL  ',
    '     OLHHLHHAAAAHHHBDL  ',
    '     OLHHHAAAAAAAABBDL  ',
    '     OLHHAAAAAAAABBBDL  ',
    '     OLHAAAAAAAABBBBDL  ',
    '     OHAAAAAAAABBBBBDL  ',
    '     OHAAAAAAABBBBBBDL  ',
    '     OHCCAAAACBBBBCCDL  ',
    '     OHCCAAABCBBBBCCDL  ',
    '     OADDDDDDDDDDDDDDL  ',
    '     OOOOOOLLLLLLLLLLL  ',
    '                        ',
    '                        ',
    '                        ',
  ],
  wic_bolt: [
    '               OLLLHO   ',
    '              OOLHHBO   ',
    '             OOLLHHBO   ',
    '            OOLLCCBBO   ',
    '           OOLLCCBBOO   ',
    '          OOLLCCCBOO    ',
    '          OLLCCCBOO     ',
    '         OOLHCCBOO      ',
    '        OOLLCCBOO       ',
    '       OOLLCCAAAOO      ',
    '       OLLHCCAAAAO      ',
    '       OHHHAAAAADO      ',
    '       OOABAAAADDO      ',
    '        OOOHAAADOO      ',
    '         OOHAADDO       ',
    '         OHHAADOL       ',
    '        OOHAADDL        ',
    '       OOHHAADLL        ',
    '       OHHAADDL         ',
    '      OOHAADDLL         ',
    '      OHHABDLL          ',
    '      OHABDDL           ',
    '      OHBBDLL           ',
    '      OBDDDL            ',
  ],
  wic_crystal: [
    '             OOO        ',
    '             OHO        ',
    '            OOHOO       ',
    '          OOOLLHO       ',
    '          OCCLHAOO      ',
    '          OCCHHHAOO     ',
    '         OOCHHHHHAOO    ',
    '        OOCCHHHHAAAOO   ',
    '        OLCCHHHAAAAAO   ',
    '        OLCHHHAAAAADO   ',
    '        OHCHHAAAAADDO   ',
    '        OOLHAAAAAADOO   ',
    '         OHAAAAAAADO    ',
    '         OHAAAAAAADL    ',
    '         OAAAAAAADDL    ',
    '         OOHAAAABDLL    ',
    '          OHAAABBDL     ',
    '          OHAABBBDL     ',
    '          OBABBBDDL     ',
    '          OOHBBBDLL     ',
    '           OBDDDDL      ',
    '           OLLBLLL      ',
    '             OLL        ',
    '                        ',
  ],
  wic_orb: [
    '       OOOOOOOOO        ',
    '     OOOHHHHHHHOOO      ',
    '    OOHHHOOOOOHHAOO     ',
    '   OOHHOLLLLLLHOAAOO    ',
    '  OOHOOLLHHHHHHAOOAOO   ',
    ' OOHOLLLHHHHHHHAHAOAOO  ',
    ' OHHOLHHHHHHHHAAABOABO  ',
    'OOHOLLHHHCHHHAAAAAAOBOO ',
    'OHHLLHHHCCCHAAAAAAABBBL ',
    'OHOLHHHHCCCCAAAAAAADLBL ',
    'OHOLHHHHCCCAAAAAAABDOBL ',
    'OHOLHHHHHAAAAAAAABBDOBL ',
    'OHOLHHHHAAAAAAAABBBDODL ',
    'OHOLHHHAAAAAAAABBBBDODL ',
    'OHHHHHAAAAAAAABBBBDDDDL ',
    'OOHOAAAAAAAAABBBBDDODLL ',
    ' OAAOHAAAAAABBBBBDLDDL  ',
    ' OOAOABAAAABBBBDDDLDLL  ',
    '  OOAOOAAABBBBDDLLDLL   ',
    '   OOAAOBDDDDDDODDLL    ',
    '    OOBBBLOOOODDDLL     ',
    '     OOOBBBBDDDLLL      ',
    '       OLLLLLLLL        ',
    '                        ',
  ],
  wic_star: [
    '           OOO          ',
    '           OHO          ',
    '          OOHOO         ',
    '          OLLHO         ',
    '          OLHAO         ',
    '         OOLHBOO        ',
    '         OLLHHAO        ',
    '  OOOOOOOOLHHHBOOOOOOOO ',
    '  OHLLLLLCCCHAAHHHHHBBL ',
    '  OOHHHHHCCCAAAAAAADDLL ',
    '   OOHHHHHCCAAAAAADDLL  ',
    '    OOHBHHAAAAAADDDLL   ',
    '     OOOHAAAAAAADOLL    ',
    '       OHAAAAAAADL      ',
    '      OOHAAAAAABDLO     ',
    '      OHHAADDABBBBL     ',
    '      OHADDDOBDBBDL     ',
    '      OHDDOOOLLBBDL     ',
    '     OOADOO   OODDLO    ',
    '     OAOOO     OLLDL    ',
    '     OOO         OLL    ',
    '                        ',
    '                        ',
    '                        ',
  ],
  iic_coin: [
    '                        ',
    '          OOOOO         ',
    '       OOOOLLHOOOO      ',
    '      OOLCCCCCCCAOO     ',
    '     OOLCCHHHHHCCAOO    ',
    '    OOCCHHHHHHHHACCOO   ',
    '   OOLCHHHHHHHHAAACAOO  ',
    '   OLCHHHHHHCHAAAAACBO  ',
    '   OCCHHHHHHCAAAAAACCO  ',
    '  OOCHHHHHHCCCAAAAAACLO ',
    '  OLCHHHHCCCCCCCAAABCBL ',
    '  OLCHHHHHCCCCCAAABBCDL ',
    '  OHCHHHHACCCCCAABBBCDL ',
    '  OOCHHHAACCCCCABBBBCLL ',
    '   OCCHAAACAAACBBBBCCL  ',
    '   OACAAAAAAAABBBBBCDL  ',
    '   OOACAAAAAABBBBBCDLL  ',
    '    OOCCAAAABBBBBCCLL   ',
    '     OOACCABBBBCCDLL    ',
    '      OOBCCCCCCCDLL     ',
    '       OOOLBDDLLLL      ',
    '          OLLLL         ',
    '                        ',
    '                        ',
  ],
  iic_heart: [
    '                        ',
    '                        ',
    '                        ',
    '                        ',
    '       OOOOOOOOOOOOO    ',
    '      OOLLLLHOLLHHAOO   ',
    '     OOLCCHHHLLHAAAAOO  ',
    '     OLCCCCHHHHAAAAAAO  ',
    '     OLHCCHHHHAAAAAADO  ',
    '     OHHHHHHHAAAAAADDO  ',
    '     OOLHHHHAAAAAAADOL  ',
    '      OHHHHAAAAAAADDL   ',
    '      OOAHAAAAAAADDLL   ',
    '       OOHAAAAAAADLL    ',
    '        OAAAAAAADDL     ',
    '        OOAAAAADDLL     ',
    '         OOHAABDLL      ',
    '          OBABDDL       ',
    '          OOBDDLL       ',
    '           OOBLL        ',
    '            OLL         ',
    '                        ',
    '                        ',
    '                        ',
  ],
  iic_magnet: [
    '                        ',
    '       OOOOOOOOOOO      ',
    '      OOLLLLLLLLHOO     ',
    '     OOLLHHHHHHHHAOO    ',
    '    OOLLHHHHHHHHHAAOO   ',
    '   OOLLHHAAAABBHAAAAOO  ',
    '   OLLHHAAOOOOOAAAAAAO  ',
    '  OOLHHAAOO   OOAAAADOO ',
    '  OLLHHAOO     OOHAAABL ',
    '  OLHHAAO       OAAABDL ',
    '  OLHHAOO       OOHBBDL ',
    '  OLHHAO         OHBBDL ',
    '  OLHHBO         OABBDL ',
    '  OLHHBO         OABBDL ',
    '  OLHHBO         OABBDL ',
    '  OLHHBO         OABBDL ',
    '  OCCCCO         OEEEEL ',
    '  OCCCCO         OEEEEL ',
    '  OCCCCO         OEEEEL ',
    '  OOOOOO         OLLLLL ',
    '                        ',
    '                        ',
    '                        ',
    '                        ',
  ],
  iic_bomb: [
    '                        ',
    '                  OOOO  ',
    '                 OOCCOO ',
    '               OOOCCCCO ',
    '              OOAAACCOO ',
    '             OOHAOOOOO  ',
    '            OOHABO      ',
    '          OOOHHBOO      ',
    '       OOOOLHHABO       ',
    '      OOLLLHAAABOO      ',
    '     OOLLHHAAAAAAOO     ',
    '     OLLCCAAAAAAABO     ',
    '     OLCCCCAAAAAADL     ',
    '    OOLHCCAAAAAABDLO    ',
    '    OLHAAAAAAAABBBBL    ',
    '    OAAAAAAAAABBBDDL    ',
    '    OOHAAAAAABBBBDLL    ',
    '     OHAAAAABBBBBDL     ',
    '     OAAAAABBBBBDDL     ',
    '     OOAAABBBBBDDLL     ',
    '      OOBDDBDDDDLL      ',
    '       OOLLBDLLLL       ',
    '          OLLL          ',
    '                        ',
  ],
  iic_blade: [
    '                        ',
    '             OOO        ',
    '             OHO        ',
    '            OOHOO       ',
    '            OLEHO       ',
    '           OOLEBOO      ',
    '           OLLEHAO      ',
    '           OLHEHBO      ',
    '           OHHEBBO      ',
    '           OOHEBOO      ',
    '            OHEBO       ',
    '            OHEBO       ',
    '            OHEDO       ',
    '            OHEDO       ',
    '        OOOOOHEDOOOOO   ',
    '        OHHHHHAAHAABL   ',
    '        OHAAAAABBBBDL   ',
    '        OADDDABDDDDDL   ',
    '        OOOOOHBDLLLLL   ',
    '            OACDL       ',
    '            OCCCL       ',
    '            OCCCL       ',
    '            OOCLL       ',
    '             OLL        ',
  ],
  iic_ghost: [
    '                        ',
    '                        ',
    '           OOOOO        ',
    '         OOOLLHOOO      ',
    '        OOLLLHHLAOO     ',
    '       OOLLHHHHHHAOO    ',
    '       OLLHHHHHHAAAO    ',
    '      OOLHHHHHHAAABOO   ',
    '      OLLHEEEHAEEAAAO   ',
    '      OLHHEEEAAEEAADO   ',
    '      OLHHHHAAAAAAADO   ',
    '      OLHHHAAAAAAAADL   ',
    '      OLHHAAAAAAAABDL   ',
    '      OLHAAAAAAAABBDL   ',
    '      OHAAAAAAAABBBDL   ',
    '      OHAAAAAAABBBBDL   ',
    '      OHAAAAAABBBBBDL   ',
    '      OAAADDDDDDBBDDL   ',
    '      OOHDDOOLLLBBDLL   ',
    '       OADOO   OODDL    ',
    '       OOOO     OLLL    ',
    '                        ',
    '                        ',
    '                        ',
  ],
  cic_install: [
    '                        ',
    '          OOOOO         ',
    '          OLLHO         ',
    '          OLHAO         ',
    '          OLHAO         ',
    '          OLHBO         ',
    '          OLHBO         ',
    '          OLHBO         ',
    '          OLHBO         ',
    '      OOOOOHABOOOO      ',
    '      OHLLHHAAHHAO      ',
    '      OOAHAAAAADDO      ',
    '       OOAAACADDOO      ',
    '        OOHCCCDOO       ',
    '   OOOOOOOACCCDOOOOOOO  ',
    '   OLHHAOOOACDOLOAAADL  ',
    '   OHAABO OOBOL OABBDL  ',
    '   OHAABO  OOL  OABDDL  ',
    '   OHAADOOOOOOOOOADDDL  ',
    '   OHAAAHHHAAAAAAADDDL  ',
    '   OHAAAABBBBBBBDDDDDL  ',
    '   OADDDDDDDDDDDDDDDDL  ',
    '   OOOOOLLLLLLLLLLLLLL  ',
    '                        ',
  ],
  astronaut_heavy: [
    '                        ',
    '           OOO          ',
    '         OOOHOOO        ',
    '         OHHHHAO        ',
    '        OOVVVVVOO       ',
    '        OHVVVVVAO       ',
    '   OOOOOOOVVVVVOOOOOOO  ',
    '   OHHHHHOHAAABOHHAABO  ',
    '  OOHHHHHHHAAAAHHAAABOO ',
    '  OHHHHHHAAAAAAAAAABBBO ',
    '  OHHHHHAAAACAAAAABBBBO ',
    '  OOHHHAAAACCCAAABBBBOO ',
    '   OHHAAAACCCCCABBBBBO  ',
    '   OHAAAAAACCCABBBBBBO  ',
    '   OHAAAAAAACABBBBBBBO  ',
    '  OOHAAAAAAAABBBBBBBBOO ',
    '  OHHAAAAAAABBBBBBBBBBO ',
    '  OHAAAAAAABBBBBBBBBBBO ',
    '  OAAABBAABBOABBBBBBBBO ',
    '  OOABBOABBBOABBBOBBBOO ',
    '   OOOOOABBBOABBBOOOOO  ',
    '       OABBBOABBBO      ',
    '       OBBBBOBBBBO      ',
    '       OOOOOOOOOOO      ',
  ],
  astronaut_mage: [
    '            OOO         ',
    '            OHO         ',
    '           OOHOO        ',
    '          OOHHAOO       ',
    '         OOHHHHAOO      ',
    '         OHHHHAAAO      ',
    '        OOHHHAAABOO     ',
    '       OOHHVVVVVAAOO    ',
    '       OHHHVVVVVAAAO    ',
    '       OAHACCVCCABBO    ',
    '       OOHAVVVVVABOO    ',
    '        OHAVVVVVABO     ',
    '       OOHAAAAAAABOO    ',
    '       OHHAAAAAABBBO    ',
    '       OHAAAACABBBBO    ',
    '      OOHAAACCCBBBBOO   ',
    '      OHHAAACCCBBBBBO   ',
    '     OOHAAAABCBBBBBBOO  ',
    '     OHHAAABBBBBBBBBBO  ',
    '     OHAAABBBBBBBBBBBO  ',
    '     OAAABBBBBBBBBBBBO  ',
    '     OABBBBBBBBBBBBBBO  ',
    '     OOOOOOOOOOOOOOOOO  ',
    '                        ',
  ],
  astronaut_drone: [
    '                        ',
    '                        ',
    '                        ',
    '         OOOOOOO        ',
    '        OOHHHHAOO       ',
    '       OOHHHHAAAOO      ',
    '   OOOOOHHHHAAAAAOOOOO  ',
    '  OOHHHHHHHCVVAAAHAABOO ',
    '  OHHHHHHHCCVVVAAAAABBO ',
    '  OHHHHHHAVVVVVAAAABBBO ',
    '  OHHHHHAAVVVVVAAABBBBO ',
    '  OOHAAAAAAVVVAABBBBBOO ',
    '   OOOOOAAAAAAABBOOOOO  ',
    '       OOAAAAABBOO      ',
    '        OOHAAABOO       ',
    '         OCCCCCO        ',
    '         OCCCCCO        ',
    '         OOCCCOO        ',
    '          OCCCO         ',
    '          OCCCO         ',
    '          OCCCO         ',
    '          OOOOO         ',
    '                        ',
    '                        ',
  ],
  boss_golem: [
    '                        ',
    '        OOOOOOO         ',
    '       OOHHHHAOO        ',
    '       OHHHHHHAO        ',
    '       OHEEHEEBO        ',
    '  OOOOOOHEEHEEBOOOOOO   ',
    ' OOHHHOOHHHAABBOOHAAOO  ',
    'OOHHHHHHOAABBBOHHAAABOO ',
    'OHHHHHHAOOOOOOOHAAABBBO ',
    'OHHHHHHHHHHHHHHAAABBBBO ',
    'OHHHHHHAAAAAAAAAABBBBBO ',
    'OOHHHHAAAAACAAAABBBBBOO ',
    'OOHHHAAAAACCCAABBBBBBOO ',
    'OHHHAAAAACCCCCBBBBBBBBO ',
    'OHHAAAAAAACCCBBBBBBBBBO ',
    'OHAAAAAAAAACBBBBBBBBBBO ',
    'OHAAAAAAAAABBBBBBBBBBBO ',
    'OHAAAAAAAABBBBBBBBBBBBO ',
    'OHAABBAAABBBBBBBBBBBBBO ',
    'OAABBOAABBBOABBBBOBBBBO ',
    'OOABOOABBBBOABBBBOOBBOO ',
    ' OOOOOABBBBOABBBBOOOOO  ',
    '     OBBBBBOBBBBBO      ',
    '     OOOOOOOOOOOOO      ',
  ],
  boss_hydra: [
    '          OOOOO         ',
    '         OOHHHOO        ',
    '   OOOOO OHHEHAO OOOOO  ',
    '  OOHHHOOOHHEHAOOOHHAOO ',
    '  OHHEHHOOHHCAAOOHHEAAO ',
    '  OHHEHAOOOHCAOOOHAEABO ',
    '  OHHCHAO OHABO OHACBBO ',
    '  OOHCHAO OHABO OHACBOO ',
    '   OHHHAOOOHABOOOAABBO  ',
    '   OOHHHAOOHABOOAAABOO  ',
    '    OHHHAOOHABOOAABBO   ',
    '    OAHABOOHABOOABBBO   ',
    '    OOHAAAOHABOAABBOO   ',
    '     OHAAAHHAAAABBBO    ',
    '     OAAAAAAAABBBBBO    ',
    '     OOHAAAAABBBBBOO    ',
    '      OHAAAABBBBBBO     ',
    '      OHAAABBBBBBBO     ',
    '      OAAABBBBBBBBO     ',
    '      OAABBBBBBBBBO     ',
    '      OBBBBBBBBBBBO     ',
    '      OOBBBBBBBBBOO     ',
    '       OOBBBBBBBOO      ',
    '        OOOBBBOOO       ',
  ],
  boss_lich: [
    '        OOO OOO OOO     ',
    '        OCO OCO OCO     ',
    '       OOCOOOCOOOCOO    ',
    '       OCCCHCCCHCCCO    ',
    '       OOOHHHHHABOOO    ',
    '        OHHHHHAAAAO     ',
    '        OHEEEAEEEBO     ',
    '        OHEEEAEEEBO     ',
    '        OAHAAEAABBO     ',
    '        OOAAAEABBOO     ',
    '        OOOHAEABOOO     ',
    '        OHHHAAAAABO     ',
    '       OOHAAAAAAABOO    ',
    '       OHHAAAAAABBBO    ',
    '       OHAAAAAABBBBO    ',
    '       OHAAACCCBBBBO    ',
    '      OOHAAACCCBBBBOO   ',
    '      OHHAAACCCBBBBBO   ',
    '      OHAAABCCCBBBBBO   ',
    '      OAAABBBBBBBBBBO   ',
    '     OOABBBBBBBBBBBBOO  ',
    '     OAOOABOABOOBBOBBO  ',
    '     OBOOBBOBBOOBBOBBO  ',
    '     OOOOOOOOOOOOOOOOO  ',
  ],
  boss_queen: [
    '       OCCOO OOCCO      ',
    '       OOCCOOOCCOO      ',
    '  OOO   OOCCCCCOO   OOO ',
    '  OHOOO  OHCCCAO   OOAO ',
    '  OOHHOO OHEHEAO OOOAAO ',
    '   OHHHOOOHHHABOOOHHAOO ',
    '   OOHHHHOHHAABOHHHBBO  ',
    '    OHAHHHCAAACHHABBOO  ',
    '    OOOHAHAAAAAABBBOO   ',
    '      OOOHAAAAABBOOO    ',
    '      OOOHAAAAABOOO     ',
    '     OOAAHAAAAABBBOO    ',
    '   OOOAAOHAAAAABOBBOOO  ',
    '   OAAOHHHAAAAABABOBBO  ',
    '   OOOAHAAAAAABBBBBOOO  ',
    '   OAAOEEEEEEEEEEEOBBO  ',
    '   OOOAHAAAABBBBBBBOOO  ',
    '   OAAOHAAABBBBBBBOBBO  ',
    '   OOOOEEEEEEEEEEEOOOO  ',
    '      OAABBBBBBBBBO     ',
    '      OOBBBBBBBBBOO     ',
    '       OOBBBBBBBOO      ',
    '        OOOBBBOOO       ',
    '          OOOOO         ',
  ],
  boss_warlord: [
    '     OOO       OOO      ',
    '     OCOO     OOCO      ',
    '     OCCOOOOOOOCCO OOOOO',
    '     OOCCOHHAOCCOOOOHACO',
    '      OOCCHHHCCOO OHACCO',
    '       OHHHHAABO OOACCBO',
    '  OOOOOOHEEEEEBOOOAACCOO',
    ' OOHHHHOOEEEEEOOHAAABCO ',
    ' OHHHHHAOOHABOOHAAABBOO ',
    ' OHHHHHHHHHAAHHAAABBBO  ',
    ' OHHHHHAAAAAAAAAABBBBO  ',
    ' OHHHHAAAAACAAAABBBBBO  ',
    ' OOAAAAAAACCCAABBBBBOO  ',
    '  OOOHAAAACCCABBBBOOO   ',
    '    OHAAAAACABBBBBO     ',
    '    OHAAAAAABBBBBBO     ',
    '    OHAAAAABBBBBBBO     ',
    '    OABAAABBBBBBBBO     ',
    '    OOOAABBOABBBOOO     ',
    '      OABBBOABBBO       ',
    '      OABBBOABBBO       ',
    '      OABBBOABBBO       ',
    '      OBBBBOBBBBO       ',
    '      OOOOOOOOOOO       ',
  ],
  boss_rootzilla: [
    '       OOOHHHHHOOO      ',
    '      OOHHHHHHHHAOO     ',
    '      OHHHHHCCHHAAO     ',
    '     OOHHHHHCCHAABOO    ',
    '     OHHCCHHHHAAAAAO    ',
    '     OHHHHHHHAACCABO    ',
    '  OOOOHHHHHHAAACCABOOOO ',
    '  OHHOHHHHHAAAAAABBOABO ',
    '  OHHHOHHHAAAAAAABOAABO ',
    '  OHAHHHHAAAAAAAAAABBBO ',
    '  OOOHHHAAAAAAAAAABBOOO ',
    '    OOAAAAAAAAAAABBOO   ',
    '     OOABAEAAAEBBBOO    ',
    '      OOOEEEAEEEOOO     ',
    '        OHEAAAEBO       ',
    '        OHAAABBBO       ',
    '        OAAABBBBO       ',
    '        OAABBBBBO       ',
    '        OABBBBBBO       ',
    '        OABBBBBBO       ',
    '       OOABBBBBBOO      ',
    '      OOABBOOOBBBOO     ',
    '     OOABBOO OOBBBOO    ',
    '     OBBBOO   OOBBBO    ',
  ],
  boss_sandwyrm: [
    '              OOO       ',
    '           OOOOHOOOOO   ',
    '          OOHHHHHHCCO   ',
    '          OCCHCCCACCO   ',
    '          OHHCEEECABO   ',
    '         OOHCEEEEECBOO  ',
    '         OHHCEEEEECBAO  ',
    '         OOHCEEEEECBOO  ',
    '        OOHHACEEECABO   ',
    '       OOHHAAACCCABBO   ',
    '      OOHHAAAAABBBBOO   ',
    '      OHHAAAABBBOOOO    ',
    '      OHAAAAABOOO       ',
    '      OAAAAAAAAOO       ',
    '      OOAAAAAAABOOO     ',
    '       OOAAAAABBABOO    ',
    '        OOAAABBBBBBO    ',
    '         OOABBBBBBBO    ',
    '         OOABBBBBBBO    ',
    '        OOAABBBBBBBO    ',
    '       OOAABBBBBBBOO    ',
    '       OAABBBBBBBOO     ',
    '       OABBBBBBBOO      ',
    '       OBBBBBBBOO       ',
  ],
  boss_reaper: [
    '                  OOOOO ',
    '           OOO   OOHHAO ',
    '          OOHOOOOOCCABO ',
    '         OOHHCCCCCCCABO ',
    '        OOHHHHACCCCCBBO ',
    '        OHHHHAABOOCABOO ',
    '       OOHHEEEABOOOABO  ',
    '       OHHEEEEEAAOOABO  ',
    '       OHHCCECCABOOABO  ',
    '       OHAEEEEEABOOABO  ',
    '       OAAEEEEEBBOOABO  ',
    '       OOHAEEEABOOOABO  ',
    '       OHHAAAAAABOAABO  ',
    '       OHAAAAAABBOABBO  ',
    '      OOHAAAAABBBOABBO  ',
    '      OHHAAAABBBBAABBO  ',
    '      OHAAAABBBBBBBBBO  ',
    '      OHAAABBBBBBBBBOO  ',
    '      OAAABBBBBBBBBBO   ',
    '     OOAABBBBBBBBBBBO   ',
    '     OABBBBBBBBBBBBBO   ',
    '     OBOOBOOABOBBOBBO   ',
    '     OBOOBOOBBOBBOBOO   ',
    '     OOOOOOOOOOOOOOO    ',
  ],
  boss_forklift: [
    '                        ',
    '             OOOOO      ',
    '             OHHAO      ',
    '             OHABO      ',
    '             OHABO      ',
    '   OOOOOOOOOOOHABO      ',
    '   OHHHHHHHHHHHABO      ',
    '   OHCCCCCCCAAAABO      ',
    '   OHCCCCCCCAAAABO      ',
    '   OHCCCCCCCAAAABO      ',
    '   OHCCCCCCCAAAABO      ',
    '   OHCCCCCCCAAAABOOOOOO ',
    '   OHAAAAAAAAAABCCCCCCO ',
    '   OHAAAAAAAAABBCCCCCCO ',
    '   OHAAAAAAAABBBBOOOOOO ',
    '   OHAAAAAAABBBBCCCCCCO ',
    '   OHAEABBABEBBBCCCCCCO ',
    '   OEEEEEOEEEEEBBOOOOOO ',
    '   OEECEEOEECEEOOO      ',
    '   OEECEEOEECEEO        ',
    '   OEEEEEOEEEEEO        ',
    '   OOOEOOOOOEOOO        ',
    '     OOO   OOO          ',
    '                        ',
  ],
  boss_scorpion: [
    '                        ',
    '                        ',
    '            OOOOO       ',
    '         OOOOHHAO       ',
    '         OCCAAABOO      ',
    '         OCCAOAAAOO     ',
    '   OOOO  OOOOOOHAAOOOOO ',
    '  OOCCOO      OHABOACCO ',
    '  OHCCHOO    OOHAAAACCO ',
    '  OHHHHAOO  OOHHAAABBBO ',
    '  OHHHHHAOOOOHHAAABBBBO ',
    '  OOHAHAAHHHHHAAABBBBOO ',
    '   OOOABAAAAAAAABBBOOO  ',
    '     OOOHAAEAEABBOOO    ',
    '      OOHAAEAEBBBOO     ',
    '     OOHHAAAABBBBBOO    ',
    '    OOHHAAAABBBBBBBOO   ',
    '    OAHBBBABBBBBBBBBO   ',
    '    OOABOOBBBBBOOBBOO   ',
    '    OAAOOOOOOOOOOOBBO   ',
    '    OOOO         OOOO   ',
    '                        ',
    '                        ',
    '                        ',
  ],
  boss_fridge: [
    '                        ',
    '      OOOOOOOOOOOOOOOO  ',
    '      OHHHHHHHHHHHHHAO  ',
    '      OHHHHHHHHHAAAABO  ',
    '      OHHHHHHHHAACCABO  ',
    '      OHHHHHHHAEACCABO  ',
    '      OHHHEEHAEEECCABO  ',
    '      OHHHHHAAAEACCABO  ',
    '      OHHHHAAAAAAAAABO  ',
    '      OHHHAAAAAAAAAABO  ',
    '      OAAABBBBBBBBBBBO  ',
    '      OOOOOOOOOOOOOOOO  ',
    '      OHHHHHHHAAAAAABO  ',
    '      OHAAAAAAAABBBBBO  ',
    '      OHAACCAAABBCCBBO  ',
    '      OHACCCCABBBCCBBO  ',
    '      OHACCCCBBBBCCBBO  ',
    '      OHAACCBBBBBCCBBO  ',
    '      OHAAABBBBBBCCBBO  ',
    '      OAAABBBBBBBBBBBO  ',
    '      OAABBBBBBBBBBBBO  ',
    '      OBBBBBBBBBBBBBBO  ',
    '      OOABBOOOOOOBBBOO  ',
    '       OBBBO    OBBBO   ',
  ],
  boss_djskel: [
    '         OOOOOOO        ',
    '       OOOCCOCCOOO      ',
    '       OCCOOOOOCCO      ',
    '      OOCOHHHHAOCOO     ',
    '     OOCCHHHHHAHCCOO    ',
    '     OCCCCEEHEECCCCO    ',
    '     OCCCCEEAEECCCCO    ',
    '     OOCCAHAAABBCCOO    ',
    '      OOCOABEBBOCOO     ',
    '       OCCOOEOOCCO      ',
    '       OOHCCHCCAOO      ',
    '        OHAAAAABO       ',
    '       OOHAAAAABOO      ',
    '     OOOHHAAAAABBOOO    ',
    '   OOOHHHBBBBBBBBABOOO  ',
    '   OHHHAABOOOOOABBBABO  ',
    '  OOHAAAABOO OOABBBBBOO ',
    '  OHHACCAABO OAABCCBBBO ',
    '  OAAACCABBO OBBBCCBBBO ',
    '  OOHAAAABOO OOABBBBBOO ',
    '   OABABBBO   OBBBBBBO  ',
    '   OOOBBOOO   OOOBBOOO  ',
    '     OOOO       OOOO    ',
    '                        ',
  ],
  prop_crystal: [
    '                        ',
    '                        ',
    '           OOO          ',
    '           OAOO         ',
    '          OOHHO         ',
    '         OOAHHOO        ',
    '         OAAHHAO        ',
    '        OOAAHHAOO       ',
    '       OOAAAHHAAOO      ',
    '       OAAAAHHAAAO      ',
    '       OAAAAHHAAAO      ',
    '       OAAAAHHBBAO      ',
    '     OOOAAAAHHBBAO      ',
    '    OOAOOAAAHHBBOOOO    ',
    '   OOAAOOAAAHHBBOOAOO   ',
    '   OAAAOOAAAHHBBOOAAOO  ',
    '   OAAAOOAAAHHBBOOAAAO  ',
    '   OOAAOOAAAHHBBOOAAAO  ',
    '    OAAOOOAAHHBBOOAAOO  ',
    '    OOAO OAAHHBBOOAOO   ',
    '     OOO OAAHHBBOOOO    ',
    '         OOOOOOOO       ',
    '                        ',
    '                        ',
  ],
}

export interface SpriteEntry {
  name: string
  shape: string
  pal: Pal
}

// dark outline reused everywhere
export const O = '#14121c'

// Every entity gets its own palette → a unique sprite. Original, themed colors.
export const SPRITES: SpriteEntry[] = [
  // ---- player heroes (tinted further by character color at runtime) ----
  { name: 'hero', shape: 'astronaut', pal: { O, A: '#e2e6f0', B: '#a8b0c4', H: '#ffffff', V: '#1a2c5a', C: '#8a93a8' } },
  { name: 'hero_heavy', shape: 'astronaut_heavy', pal: { O, A: '#e2e6f0', B: '#a8b0c4', H: '#ffffff', V: '#1a2c5a', C: '#8a93a8' } },
  { name: 'hero_mage', shape: 'astronaut_mage', pal: { O, A: '#e2e6f0', B: '#a8b0c4', H: '#ffffff', V: '#1a2c5a', C: '#8a93a8' } },
  { name: 'hero_drone', shape: 'astronaut_drone', pal: { O, A: '#e2e6f0', B: '#a8b0c4', H: '#ffffff', V: '#1a2c5a', C: '#8a93a8' } },

  // ---- enemies ----
  { name: 'enemy:grub', shape: 'slime', pal: { O, A: '#7ec850', B: '#4f8f30', H: '#c2f09a', E: '#14121c' } },
  { name: 'enemy:runt', shape: 'slimesmall', pal: { O, A: '#e0d24a', B: '#b0a020', H: '#fff7b0', E: '#14121c' } },
  { name: 'enemy:swarmling', shape: 'slimesmall', pal: { O, A: '#e08a4a', B: '#b05a20', H: '#ffd0a0', E: '#14121c' } },
  { name: 'enemy:brute', shape: 'biped', pal: { O, A: '#c0613a', B: '#8a3a1e', H: '#f0a070', E: '#ffe080' } },
  { name: 'enemy:dasher', shape: 'biped', pal: { O, A: '#46c8d8', B: '#1f8a98', H: '#a0f0ff', E: '#ffffff' } },
  { name: 'enemy:blob', shape: 'slime', pal: { O, A: '#5ad0a0', B: '#2f9070', H: '#a0ffd8', E: '#14121c' } },
  { name: 'enemy:blobling', shape: 'slimesmall', pal: { O, A: '#7ee0b8', B: '#4faf88', H: '#c0ffe0', E: '#14121c' } },
  { name: 'enemy:wisp', shape: 'flyer', pal: { O, A: '#b18cff', B: '#7a5ad0', H: '#e0d0ff', E: '#ffffff' } },
  { name: 'enemy:reaver', shape: 'biped', pal: { O, A: '#d23a6a', B: '#901f44', H: '#ff80a0', E: '#ffe080' } },
  { name: 'enemy:spitter', shape: 'caster', pal: { O, A: '#9b59b6', B: '#6a2f80', H: '#d0a0e0', C: '#6affb0', E: '#6affb0' } },
  { name: 'enemy:sniper', shape: 'caster', pal: { O, A: '#ff7a3d', B: '#c04a18', H: '#ffc0a0', C: '#ffffff', E: '#ffffff' } },
  { name: 'enemy:archer', shape: 'caster', pal: { O, A: '#d0a050', B: '#9a7020', H: '#ffe0a0', C: '#a0e0ff', E: '#a0e0ff' } },
  { name: 'enemy:summoner', shape: 'caster', pal: { O, A: '#b060d0', B: '#7a30a0', H: '#e0b0ff', C: '#ff80c0', E: '#ff80c0' } },
  { name: 'enemy:ghoul', shape: 'biped', pal: { O, A: '#88aa66', B: '#587038', H: '#c0e0a0', E: '#ff5050' } },
  { name: 'enemy:stalker', shape: 'flyer', pal: { O, A: '#9c6cd0', B: '#6a3aa0', H: '#d0b0ff', E: '#ff4040' } },
  { name: 'enemy:shielder', shape: 'biped', pal: { O, A: '#6688aa', B: '#3a5878', H: '#a0c0e0', E: '#ffe080' } },
  { name: 'enemy:bomber', shape: 'bomb', pal: { O, A: '#ff5a2d', B: '#c03010', H: '#ffb080', C: '#ffe000', E: '#14121c' } },
  { name: 'enemy:crystalback', shape: 'crystal', pal: { O, A: '#66d0c0', B: '#2f9088', H: '#c0fff0', C: '#d6fff6', E: '#14121c' } },
  { name: 'enemy:shard', shape: 'crystal', pal: { O, A: '#9fe0d4', B: '#5fafa0', H: '#e0fff8', C: '#f0fffc', E: '#14121c' } },
  { name: 'enemy:revenant', shape: 'skelly', pal: { O, A: '#b89070', B: '#80604a', H: '#e8d0b0', E: '#ff3030' } },
  { name: 'enemy:scuttle', shape: 'biped', pal: { O, A: '#d08050', B: '#90501f', H: '#ffb080', E: '#ffe080' } },
  { name: 'enemy:locust', shape: 'slimesmall', pal: { O, A: '#d4b83a', B: '#8a7420', H: '#f0e07a', E: '#14121c' } },
  { name: 'enemy:spittoad', shape: 'caster', pal: { O, A: '#9ed13b', B: '#5e7e22', H: '#d4f26b', C: '#ff8a2a', E: '#ff8a2a' } },
  { name: 'enemy:imp', shape: 'caster', pal: { O, A: '#8fa85a', B: '#566834', H: '#c4d68f', C: '#e04a2a', E: '#e04a2a' } },
  { name: 'enemy:roomba', shape: 'slimesmall', pal: { O, A: '#c0c0d0', B: '#7a7a90', H: '#eef0f8', E: '#46e0ff' } },
  { name: 'enemy:boar', shape: 'biped', pal: { O, A: '#c08060', B: '#80502f', H: '#e8b090', E: '#ff5050' } },
  { name: 'enemy:batcloud', shape: 'flyer', pal: { O, A: '#6a5a8a', B: '#3f3458', H: '#b0a0d0', E: '#ff4040' } },
  { name: 'enemy:magmabrute', shape: 'biped', pal: { O, A: '#c0502e', B: '#6e2a18', H: '#ff9050', E: '#ffd23b' } },
  { name: 'enemy:voodoo', shape: 'caster', pal: { O, A: '#b060d0', B: '#7a30a0', H: '#e0b0ff', C: '#a8e80c', E: '#a8e80c' } },
  { name: 'enemy:ironlich', shape: 'skelly', pal: { O, A: '#8fa0b5', B: '#566878', H: '#c8d4e0', E: '#5be0c8' } },
  { name: 'enemy:yetibull', shape: 'biped', pal: { O, A: '#cfe2ff', B: '#8aa8d0', H: '#ffffff', E: '#1fe3d0' } },
  { name: 'enemy:bogtroll', shape: 'biped', pal: { O, A: '#4a7a4a', B: '#2c4e2c', H: '#7fc062', E: '#ffd23b' } },
  // behavior-diverse roster (#missed3): armored / buffer / flanker
  { name: 'enemy:aegis', shape: 'biped', pal: { O, A: '#7f8fb0', B: '#465670', H: '#cfe0ff', E: '#46e0ff' } },
  { name: 'enemy:warchanter', shape: 'caster', pal: { O, A: '#e0a33a', B: '#9a6e1e', H: '#ffe6a0', C: '#ff3a2a', E: '#ffffff' } },
  { name: 'enemy:overseer', shape: 'caster', pal: { O, A: '#c04ad0', B: '#7a2a8a', H: '#f0b0ff', C: '#ffd23b', E: '#ffd23b' } },
  { name: 'enemy:razorwing', shape: 'flyer', pal: { O, A: '#ff6a5a', B: '#b03a2e', H: '#ffc0b0', E: '#ffffff' } },
  { name: 'enemy:flankwraith', shape: 'flyer', pal: { O, A: '#3fd0c0', B: '#1f8a80', H: '#a0fff0', E: '#ff4040' } },

  // ---- bosses ----
  { name: 'enemy:boss_golem', shape: 'boss_golem', pal: { O, A: '#9aa3b0', B: '#5f6873', H: '#d0d8e0', C: '#ffc040', E: '#ff5050' } },
  { name: 'enemy:boss_hydra', shape: 'boss_hydra', pal: { O, A: '#6ad06a', B: '#2f8a2f', H: '#b0ffb0', C: '#ffe000', E: '#ff5050' } },
  { name: 'enemy:boss_lich', shape: 'boss_lich', pal: { O, A: '#c8b0ff', B: '#8a6ad0', H: '#f0e0ff', C: '#60ffe0', E: '#60ffe0' } },
  { name: 'enemy:boss_queen', shape: 'boss_queen', pal: { O, A: '#d060a0', B: '#9a2f70', H: '#ffb0e0', C: '#ffe000', E: '#ffffff' } },
  { name: 'enemy:boss_warlord', shape: 'boss_warlord', pal: { O, A: '#cc5533', B: '#902f18', H: '#ff9070', C: '#ffd040', E: '#ffe080' } },
  { name: 'enemy:boss_rootzilla', shape: 'boss_rootzilla', pal: { O, A: '#3f9a4e', B: '#1f6030', H: '#7fd060', C: '#ffd23b', E: '#ff5050' } },
  { name: 'enemy:boss_sandwyrm', shape: 'boss_sandwyrm', pal: { O, A: '#c1502e', B: '#7a3017', H: '#e8835a', C: '#ffd23b', E: '#ffe080' } },
  { name: 'enemy:boss_reaper', shape: 'boss_reaper', pal: { O, A: '#5be0c8', B: '#2a8a7a', H: '#b0f7ec', C: '#ff477e', E: '#ffffff' } },
  { name: 'enemy:boss_forklift', shape: 'boss_forklift', pal: { O, A: '#ffd23b', B: '#a8832a', H: '#fff0a0', C: '#46e0ff', E: '#14121c' } },
  { name: 'enemy:boss_scorpion', shape: 'boss_scorpion', pal: { O, A: '#c8923a', B: '#7a5520', H: '#e8c46a', C: '#ff6a2a', E: '#ff3030' } },
  { name: 'enemy:boss_fridge', shape: 'boss_fridge', pal: { O, A: '#c0c6d0', B: '#7a8290', H: '#eef4ff', C: '#46e0ff', E: '#1fe3d0' } },
  { name: 'enemy:boss_djskel', shape: 'boss_djskel', pal: { O, A: '#b060d0', B: '#7a30a0', H: '#e0b0ff', C: '#a8e80c', E: '#ffffff' } },
  // curse>=5 final boss (was invisible — no sprite entry); void-tinted skull-lord
  { name: 'enemy:boss_voidlord', shape: 'boss_lich', pal: { O, A: '#9b3cff', B: '#5f2090', H: '#d0a0ff', C: '#60ffe0', E: '#ffffff' } },

  // ---- pickups ----
  { name: 'pickup:xp', shape: 'gem', pal: { O, A: '#2f8fff', B: '#1f5fb0', H: '#a0d8ff' } },
  // tiered XP gems (#missed6): small blue → medium green → large amethyst
  { name: 'pickup:xp1', shape: 'gem', pal: { O, A: '#2f8fff', B: '#1f5fb0', H: '#a0d8ff' } },
  { name: 'pickup:xp2', shape: 'gem', pal: { O, A: '#33d98a', B: '#1f8f58', H: '#b0ffd0' } },
  { name: 'pickup:xp3', shape: 'gem', pal: { O, A: '#b65cff', B: '#7a2fc0', H: '#e8c0ff' } },
  { name: 'pickup:gold', shape: 'coin', pal: { O, A: '#ffcc33', B: '#c89010', H: '#fff0a0', C: '#a06f10' } },
  { name: 'pickup:heal', shape: 'heart', pal: { O, A: '#ff3b5c', B: '#b01f3a', H: '#ff90a0' } },
  { name: 'pickup:chest', shape: 'chest', pal: { O, A: '#c89b3c', B: '#8a6a20', H: '#ffe080', C: '#ffcc33' } },
  { name: 'pickup:magnet', shape: 'magnet', pal: { O, A: '#d23a4a', B: '#901f2a', H: '#ff90a0', C: '#c8c8d0' } },
  { name: 'pickup:bomb', shape: 'bomb', pal: { O, A: '#ff7a18', B: '#c04a08', H: '#ffc080', C: '#ffe000', E: '#14121c' } },
  { name: 'pickup:vendor', shape: 'caster', pal: { O, A: '#7a5a3a', B: '#503820', H: '#c8a060', C: '#ffcc33', E: '#ffcc33' } },
  { name: 'prop:shrine', shape: 'shrine', pal: { O, A: '#d8d8e8', B: '#9098b0', H: '#ffffff' } },
  { name: 'prop:moai', shape: 'moai', pal: { O, A: '#9aa0ac', B: '#5a606c', H: '#c8cdd6', E: '#14121c' } },
  { name: 'prop:microwave', shape: 'microwave', pal: { O, A: '#8a90a0', H: '#46e0ff', C: '#ffd23d', E: '#14121c' } },
  { name: 'prop:portal', shape: 'shrine', pal: { O, A: '#1fe3d0', B: '#0c8f86', H: '#bafff7' } },
  { name: 'prop:shrine_magnet', shape: 'shrine', pal: { O, A: '#b46cff', B: '#7a3fd0', H: '#e6d2ff' } },
  { name: 'prop:shrine_gold', shape: 'shrine', pal: { O, A: '#ffd23d', B: '#c89a20', H: '#fff0b0' } },
  { name: 'prop:shrine_heal', shape: 'shrine', pal: { O, A: '#5be08a', B: '#2f9a5a', H: '#c8ffd8' } },
  { name: 'prop:shrine_treasure', shape: 'shrine', pal: { O, A: '#ffb13d', B: '#c87a20', H: '#ffe0a0' } },
  { name: 'prop:shrine_battle', shape: 'shrine', pal: { O, A: '#ff5a6a', B: '#c03040', H: '#ffc8ce' } },
  { name: 'prop:shrine_curse', shape: 'shrine', pal: { O, A: '#ff2e97', B: '#a01060', H: '#ffb6e2' } },
  { name: 'prop:tree', shape: 'tree', pal: { O, A: '#3f7a2e', B: '#6a4a2a', H: '#6fc04a' } },
  { name: 'prop:bush', shape: 'bush', pal: { O, A: '#3f7a2e', H: '#6fc04a' } },
  { name: 'prop:rock', shape: 'rock', pal: { O, A: '#8a8f98', B: '#5a5f68', H: '#c0c4cc' } },
  { name: 'prop:cactus', shape: 'cactus', pal: { O, A: '#3f9a4e', H: '#7fd060' } },
  { name: 'prop:grave', shape: 'grave', pal: { O, A: '#9098a0', B: '#5a5040', H: '#c4ccd4' } },
  { name: 'prop:deadtree', shape: 'tree', pal: { O, A: '#5a4a3a', B: '#4a3a2a', H: '#7a6a5a' } },
  { name: 'prop:pillar', shape: 'pillar', pal: { O, A: '#b4a0d8', B: '#7a68a0', H: '#e0d0ff' } },
  { name: 'prop:crystal', shape: 'prop_crystal', pal: { O, A: '#b46cff', B: '#7a3aa0', H: '#e0c0ff', E: '#ffffff' } },
  // space scenery
  { name: 'prop:crater', shape: 'crater', pal: { O, A: '#4a4a5c', B: '#2c2c3a', H: '#6a6a80' } },
  { name: 'prop:moonrock', shape: 'rock', pal: { O, A: '#8a8f9a', B: '#4e535c', H: '#c0c6d0' } },
  { name: 'prop:redrock', shape: 'rock', pal: { O, A: '#a8512b', B: '#6e2a18', H: '#e0835a' } },
  { name: 'prop:flag', shape: 'flag', pal: { O, A: '#c0c6d0', H: '#5fe6ff', C: '#ff2e97' } },
  { name: 'prop:satellite', shape: 'satellite', pal: { O, A: '#c0c6d0', H: '#eef4ff', C: '#1fe3d0' } },
  { name: 'prop:debris', shape: 'rock', pal: { O, A: '#5a6270', B: '#33394a', H: '#8a93a8' } },
  { name: 'prop:crate', shape: 'crate', pal: { O, A: '#c8923a', B: '#8a6a20', H: '#e8c46a' } },
  { name: 'prop:antenna', shape: 'antenna', pal: { O, A: '#9aa0ac', H: '#eef4ff', C: '#1fe3d0' } },
  // breakables (smash for loot)
  { name: 'break:crate', shape: 'crate', pal: { O, A: '#9aa0ac', B: '#5a5f68', H: '#d0d6e0' } },
  { name: 'break:pod', shape: 'bomb', pal: { O, A: '#5fbf5f', B: '#357a35', H: '#9ae89a', C: '#a8e80c', E: '#14121c' } },
  { name: 'break:barrel', shape: 'crate', pal: { O, A: '#c8923a', B: '#8a6a20', H: '#e8c46a' } },

  // ---- projectiles (white-ish; runtime tint = weapon color) ----
  { name: 'proj:orb', shape: 'orb', pal: { O, A: '#e8e8f0', B: '#b0b0c0', H: '#ffffff' } },
  { name: 'proj:blade', shape: 'blade', pal: { O, A: '#e8e8f0', B: '#b0b0c0', H: '#ffffff' } },
  { name: 'proj:bolt', shape: 'bolt', pal: { O, A: '#e8e8f0', B: '#b0b0c0', H: '#ffffff' } },
  { name: 'proj:star', shape: 'star', pal: { O, A: '#e8e8f0', B: '#b0b0c0', H: '#ffffff' } },
]

export interface AtlasResult {
  texture: THREE.CanvasTexture
  uv: Map<string, { u0: number; v0: number; u1: number; v1: number }>
  cols: number
  rows: number
}

/** Bake every SpriteEntry into a single atlas canvas texture. */
export function buildAtlas(): AtlasResult {
  const n = SPRITES.length
  const cols = Math.ceil(Math.sqrt(n))
  const rows = Math.ceil(n / cols)
  const canvas = document.createElement('canvas')
  canvas.width = cols * CELL
  canvas.height = rows * CELL
  const ctx = canvas.getContext('2d')!
  ctx.imageSmoothingEnabled = false

  const uv = new Map<string, { u0: number; v0: number; u1: number; v1: number }>()

  for (let i = 0; i < n; i++) {
    const def = SPRITES[i]
    const shape = SHAPES[def.shape]
    const cx = (i % cols) * CELL
    const cy = Math.floor(i / cols) * CELL
    const h = shape.length
    const w = Math.max(...shape.map((r) => r.length))
    const ox = Math.floor((CELL - w) / 2)
    const oy = Math.floor((CELL - h) / 2)
    for (let r = 0; r < h; r++) {
      const row = shape[r]
      for (let c = 0; c < row.length; c++) {
        const key = row[c]
        if (key === ' ' || key === '.') continue
        const color = def.pal[key]
        if (!color) continue
        ctx.fillStyle = color
        ctx.fillRect(cx + ox + c, cy + oy + r, 1, 1)
      }
    }
    uv.set(def.name, {
      u0: cx / canvas.width,
      v0: cy / canvas.height,
      u1: (cx + CELL) / canvas.width,
      v1: (cy + CELL) / canvas.height,
    })
  }

  const texture = new THREE.CanvasTexture(canvas)
  texture.magFilter = THREE.NearestFilter
  texture.minFilter = THREE.NearestFilter
  texture.generateMipmaps = false
  texture.flipY = false
  texture.colorSpace = THREE.SRGBColorSpace
  texture.needsUpdate = true

  return { texture, uv, cols, rows }
}

/** Map a runtime entity to a sprite atlas name (with sensible fallbacks). */
export function enemySpriteName(defId: string): string {
  return `enemy:${defId}`
}

// AO5 — per-character hero body archetype (still color-tinted per character). Default = the standard astronaut.
const HERO_ARCHETYPE: Record<string, string> = {
  tank: 'hero_heavy', juggernaut: 'hero_heavy', berserker: 'hero_heavy',
  witch: 'hero_mage', frost: 'hero_mage', vampire: 'hero_mage', executioner: 'hero_mage',
  engineer: 'hero_drone', swarm: 'hero_drone',
}
export function heroSpriteName(charId: string): string {
  return HERO_ARCHETYPE[charId] ?? 'hero'
}
export function pickupSpriteName(kind: string): string {
  return `pickup:${kind}`
}
/** Choose a projectile sprite shape by weapon family. */
export function projSpriteName(ownerWeapon: string): string {
  switch (ownerWeapon) {
    case 'boomeraxe':
    case 'stormglaive':
    case 'sawblade':
    case 'lash':
    case 'hydralash':
    case 'frostlance':
    case 'glacialspear':
    case 'plasmacutter':
    case 'plasmavortex':
      return 'proj:blade'
    case 'zapper':
    case 'tempest':
    case 'railgun':
    case 'annihilator':
    case 'revolver':
    case 'gatling':
    case 'prism':
    case 'spectrum':
    case 'photonlance':
    case 'photonstorm':
      return 'proj:bolt'
    case 'tome':
    case 'meteor':
    case 'apocalypse':
    case 'minefield':
    case 'novamortar':
    case 'supernova':
    case 'cryobomb':
    case 'absolutezero':
    case 'starshards':
    case 'starfall':
      return 'proj:star'
    default:
      return 'proj:orb'
  }
}
